import { Fragment, useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Card, Typography, Tag, Button, Popconfirm, Space, Select, Input, Textarea, DatePicker, Table, Modal, InputNumber, Checkbox, Tooltip, EntityAvatar, CodeChip, message, cn, type TagTone } from '../../ui';
import { productImage } from '../../lib/imageUrls';
import { warehouseOption, warehouseText, WarehouseCode } from '../../lib/warehouseLabel';
import { Plus, Package, CalendarClock, Download, Truck, PackageCheck, PackageSearch, AlertTriangle, Ban, ArrowRight, Wand2, Eraser, CheckCheck, Boxes, Search, UserRound, MessageSquare } from 'lucide-react';
import dayjs from 'dayjs';
import { disableFromDate, disableToDate } from '../../lib/dateRange';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { useSearchParams } from 'react-router-dom';
import { useUrlString, useUrlDateRange } from '../../hooks/useUrlState';
import { FilterRange, useFilterPanel } from '../../components/FilterPanel';
import { IDENTITY_SERIES } from '../../lib/palette';
import { usePermission, useAnyPermission, useHasRole } from '../../store/auth';
import { useWarehouseLookup } from '../../hooks/useWarehouseLookup';
import { useBadges } from '../../hooks/useBadges';
import { useServerSort } from '../../hooks/useServerSort';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { useFocusRow } from '../../hooks/useFocusRow';
import { TransferFormModal } from './TransferFormModal';
import { TransferDetailModal } from './TransferDetailModal';
import { ExportDialog } from '../../components/ExportDialog';
import { UserLink } from '../../components/EntityLinks';
import { ProductThumb } from '../../components/ProductThumb';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { formatIst, formatIstDate, formatIstTime } from '../../lib/time';
import { fmtQty, isRedundantSku, productLabel } from '../../lib/format';
import { ageBucket, waitingDays, waitingLabel, type AgeBucket } from '../../lib/waiting';

const COLOR: Record<string, TagTone> = {
  DRAFT: 'neutral', REQUESTED: 'blue', SOURCE_ACK: 'cyan', PREPARED: 'blue',
  IN_TRANSIT: 'gold', RECEIVED: 'green', PARTIAL: 'red', CANCELLED: 'red', REJECTED: 'red',
};
const STATUSES = ['REQUESTED', 'SOURCE_ACK', 'PREPARED', 'IN_TRANSIT', 'RECEIVED', 'PARTIAL', 'CANCELLED'];
/* WHAT EACH STATE MEANS, in words — for the filter's hover text and for what it searches on.
   The picker still SHOWS the register's own tag, so filter and column say the same thing and cannot
   drift; this is the plain-English half, which is what "SOURCE ACK" does not give a reader who has
   not met it before. Same wording the GRN timeline uses for the same stages. */
const STATUS_MEANING: Record<string, string> = {
  REQUESTED: 'Requested — waiting on the source depot',
  SOURCE_ACK: 'Source acknowledged',
  PREPARED: 'Picked and packed, not yet gone',
  IN_TRANSIT: 'Dispatched — in transit',
  RECEIVED: 'Received in full',
  PARTIAL: 'Received with a difference',
  CANCELLED: 'Cancelled',
};
/* The app's one ageing scale (lib/waiting) in Tag's vocabulary. Tag has no orange, so a few days and
   most of a week share gold; the threshold that changes the reading here is crossing a week. */
const AGE_TAG: Record<AgeBucket, TagTone> = { fresh: 'neutral', day: 'gold', ageing: 'gold', stale: 'red' };

/**
 * How many of a transfer's lines still name no batch.
 *
 * The create screen's batch pick is OPTIONAL — the source is allowed to choose lots at dispatch
 * instead — so this is a normal state, not a fault. It matters for exactly one act: marking a
 * transfer "picked and packed" claims somebody physically took cartons off a shelf, and you cannot
 * have done that without knowing which lot. The server refuses it (`TRANSFER_NO_BATCH_PICKED`); this
 * is how the row knows not to offer a button whose only possible outcome is that refusal.
 */
/**
 * "CODE · Name" for ONE END of a transfer, taken from the row the server sent.
 *
 * Always prefer the row. Resolving a warehouse id in the browser depends on the viewer's own scope,
 * and a Warehouse Manager holds no WAREHOUSE.VIEW — so the counterparty end came out as a raw `#1`
 * for them, in the register AND in every dialog that named the journey (the dispatch picker's header
 * literally read "WH-BLR · Bengaluru Warehouse → #1"). The client lookup stays as the fallback for a
 * payload that predates the server-side names.
 */
const whParts = (r: any, end: 'from' | 'to', fallback: (id: any) => string): { code: string; name: string } => {
  const code = String(r?.[`${end}WarehouseCode`] ?? '').trim();
  const name = String(r?.[`${end}WarehouseName`] ?? '').trim();
  if (code || name) return { code, name };
  /* A payload predating the server-side names: the client lookup hands back its own "CODE · Name",
     or a bare `#1` for a viewer who cannot read the warehouse master. Split what splits; a string
     with no dot is not a code, so it stays plain text rather than becoming a chip that lies. */
  const [head, ...rest] = fallback(r?.[`${end}WarehouseId`]).split(' · ');
  return rest.length ? { code: head, name: rest.join(' · ') } : { code: '', name: head };
};

/** The two halves rejoined — the plain-text form, for a sentence or a tooltip. */
const whEnd = (r: any, end: 'from' | 'to', fallback: (id: any) => string): string => {
  const { code, name } = whParts(r, end, fallback);
  return [code, name].filter(Boolean).join(' · ') || '—';
};

const unpickedLines = (r: any): number => (r?.lines ?? []).filter((l: any) => l?.batchId == null).length;

/**
 * The lots a transfer has already committed to, as a sentence — "AGN 500ml-OPENING-379 × 100".
 *
 * Exists so the pick confirmation can NAME what it is confirming. The register carries no batch
 * column (a transfer can hold many lines, so it would rarely fit), which meant the only screen that
 * mentioned the chosen lot was the detail dialog's Items table, below the fold. A confirm that cannot
 * say which lot it means is indistinguishable from one certifying nothing.
 */
const pickedLots = (r: any): string => {
  const ls = (r?.lines ?? []).filter((l: any) => l?.batchId != null);
  if (!ls.length) return '';
  const one = (l: any) => `${l.batchNo ?? `batch #${l.batchId}`} × ${fmtQty(l.qty)}`;
  return ls.length <= 2 ? ls.map(one).join(' and ') : `${one(ls[0])} and ${ls.length - 1} more lots`;
};

export function TransfersPage({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [dispatchFor, setDispatchFor] = useState<any | null>(null);     // REQUESTED transfer being dispatched (batch pick)
  const [receiveFor, setReceiveFor] = useState<any | null>(null);       // IN_TRANSIT transfer being received
  const [discrepancyFor, setDiscrepancyFor] = useState<any | null>(null); // PARTIAL transfer to resolve
  const [detailFor, setDetailFor] = useState<any | null>(null);         // row clicked open — the full record
  const [cancelFor, setCancelFor] = useState<any | null>(null);         // transfer being called off (reason required)
  const [prepareFor, setPrepareFor] = useState<any | null>(null);       // transfer whose batches are being picked
  // Each manager action is gated by its own permission (or STOCK.TRANSFER for senior roles).
  const canManage = usePermission('STOCK.TRANSFER');            // create / cancel / resolve discrepancy
  const canDispatch = useAnyPermission(['WAREHOUSE.TRANSFER.DISPATCH', 'STOCK.TRANSFER']);
  // Same gate as the page and the endpoint. A transfer register carries no pricing — it is a movement
  // record — so STOCK.VIEW / STOCK.TRANSFER is the right line, and customers hold neither.
  const [exportOpen, setExportOpen] = useState(false);
  const canReceive = useAnyPermission(['WAREHOUSE.TRANSFER.RECEIVE', 'STOCK.TRANSFER']);
  /* Whether this reader CANCELS or merely ASKS. The server enforces it either way — this only
     decides what the dialog promises, so a stale client cannot escalate itself, only mis-word a
     button. Deliberately separate from STOCK.TRANSFER: that permission now means "may ask". */
  const canApproveCancel = usePermission('STOCK.TRANSFER.CANCEL.APPROVE');
  const canAct = canDispatch || canReceive || canManage;

  // Picker options come from the scoped list; the LABEL map is topped up with the master list so a
  // transfer to a since-deactivated warehouse still prints its name. See useWarehouseLookup.
  const { options: warehouses, labelOf: whLabelOf, byId: byWhId, scopeKnown: warehouseScopeKnown } = useWarehouseLookup();

  /*
   * WHICH END OF THE MOVEMENT IS YOURS.
   *
   * The permissions above say whether you may dispatch or receive AT ALL; they say nothing about
   * WHICH warehouse. Without this a manager of the sending depot was offered Receive on a transfer
   * their own warehouse had just sent — and the server refuses it (WAREHOUSE_FORBIDDEN), so the only
   * possible outcome of pressing it was an error. It cuts both ways: a destination-only manager was
   * offered Acknowledge / Prepare / Dispatch on goods they are waiting to receive.
   *
   * Mirrors WarehouseScopeService exactly: ONLY a Warehouse Manager is row-scoped (to the warehouses
   * assigned to them); every other role is unrestricted at the scope layer, which is why this returns
   * true outright for them rather than testing the list. `/warehouses/my` does return everything for
   * a senior, but relying on that coincidence would silently start hiding buttons the day the
   * endpoint narrowed.
   *
   * A manager who holds BOTH ends of a move — an internal transfer between two of their own sites —
   * passes both tests and rightly sees the send actions now and the receive action later.
   *
   * A Super Admin keeps every button: the wildcard grants the permissions, and holding no
   * WAREHOUSE_MANAGER role leaves them unscoped here exactly as the server leaves them unscoped. The
   * one case that is scoped-and-senior is a DUAL-HAT user who genuinely wears the manager hat too —
   * and the server row-scopes them as well, so the register must not promise more than it will honour.
   */
  const isWarehouseManager = useHasRole('WAREHOUSE_MANAGER');
  const myWarehouseIds = useMemo(
    () => new Set((warehouses ?? []).map((w: any) => String(w.id))),
    [warehouses],
  );
  const myWarehouse = (id: any) => !isWarehouseManager || (id != null && myWarehouseIds.has(String(id)));

  /* What is actually WAITING on this register, named. The tab/nav badge is one number over two
     different acts — sending stock out and taking it in — and the approvals hub lists those as two
     separate lanes ("Transfer dispatch" / "Transfer receipt"), so the single badge looked like it
     disagreed with the hub. Same source as the badge (role-scoped server counts), and deliberately
     NOT computed off the filtered table: it states the page's pending work, not this filter's. */
  const badges = useBadges();
  const toDispatch = badges.transfersAwaitingDispatch ?? 0;
  const toReceive = badges.transfersAwaitingReceipt ?? 0;
  const toResolve = badges.transfersAwaitingResolution ?? 0;
  const oldestWaiting = badges.transfersOldestWaitingDays ?? 0;
  /* Each part carries the tone of the STATUS it counts (see COLOR above), so the chip and the rows
     it summarises are the same colour. A zero part is dropped, never printed as "0 to receive". */
  const waitingParts = [
    { key: 'dispatch', n: toDispatch, label: 'to dispatch', tone: COLOR.REQUESTED },
    { key: 'receive', n: toReceive, label: 'to receive', tone: COLOR.IN_TRANSIT },
    { key: 'settle', n: toResolve, label: 'to settle', tone: COLOR.PARTIAL },
  ].filter((p) => p.n > 0);

  // ── Filters ──────────────────────────────────────────────────────────────
  // Seed the search from ?q= so deep links (e.g. a GRN's STR tag) land pre-filtered to that transfer.
  const [sp] = useSearchParams();
  const [search, setSearch] = useUrlString('q');
  const [fromWh, setFromWh] = useUrlString<string | undefined>('from', undefined);
  const [toWh, setToWh] = useUrlString<string | undefined>('to', undefined);
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  /* Which of the three waiting queues the reader drilled into, from the chips above the table.
     A URL param like every other filter, so Back restores it and the view is linkable. */
  const [waiting, setWaiting] = useUrlString<string | undefined>('waiting', undefined);
  /* The rest of the register's questions, each URL-backed like every other filter so a filtered view
     is linkable and Back restores it. Grouped in the bar in COLUMN order, then the "about the
     transfer itself" set that has no column. */
  const [batch, setBatch] = useUrlString('batch');
  const [origin, setOrigin] = useUrlString<string | undefined>('origin', undefined);
  const [variance, setVariance] = useUrlString<string | undefined>('variance', undefined);
  const [frozen, setFrozen] = useUrlString<string | undefined>('frozen', undefined);
  const [productId, setProductId] = useUrlString<string | undefined>('product', undefined);
  /* EITHER BOUND ALONE IS A FILTER. This was a RangePicker, which only ever emits a COMPLETE range
     (deliberately — see the DateRange crash fix), so "everything since Monday" silently did nothing:
     you picked a start date and the register did not move. Two independent bounds, and the server
     applies each on its own (`t.transferDate >= :from`, `<= :to`). Same `from~to` query param, with
     either half allowed to be blank, so existing bookmarks still open where they used to. */
  const [range, setRange] = useUrlDateRange('range');
  // Server-side column sort (backend ORDER BY over the full result set, not just this page).
  const { sortState, onSortChange, sort } = useServerSort();

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (search.trim()) p.search = search.trim();
    if (fromWh) p.fromWarehouseId = fromWh;
    if (toWh) p.toWarehouseId = toWh;
    if (status) p.status = status;
    if (waiting) p.waiting = waiting;
    if (batch.trim()) p.batch = batch.trim();
    if (origin) p.origin = origin;
    if (variance) p.variance = variance;
    if (frozen) p.frozen = frozen;
    if (productId) p.productId = productId;
    if (range[0]) p.from = range[0].format('YYYY-MM-DD');
    if (range[1]) p.to = range[1].format('YYYY-MM-DD');
    if (sort) p.sort = sort;
    return p;
  }, [search, fromWh, toWh, status, productId, range, sort, waiting, batch, origin, variance, frozen]);

  const { data, isLoading } = useQuery({
    queryKey: ['stock-transfers', params],
    queryFn: () => request<any[]>({ url: '/stock-transfers', params }),
    placeholderData: keepPreviousData,
  });
  /* The approvals hub, notifications and the activity log all send a dispatcher or receiver HERE,
     naming the transfer they meant (?focus=…). Landing on a register of every transfer with no clue
     which one you were sent to is the reader's problem to solve; this opens the one that was linked.
     Now that the row's detail is a DIALOG rather than an expanded strip, `onFocus` opens the dialog —
     the row is still highlighted and scrolled to underneath it. */
  /*
   * WHAT THE LINK CAME HERE TO DO.
   *
   * The approvals hub sends a dispatcher / receiver / settler here for an act it cannot host itself
   * (batch picking, a line-by-line receipt), and it now says which act in `?act=`. Honouring it is
   * the difference between landing ON the job and landing NEXT TO it: without this the deep link can
   * only open the record for reading, so an approver who pressed "Pick batches" got a description of
   * the transfer and a Close button.
   *
   * An unknown or absent `act` falls through to the record, which is the right default for a link
   * that only ever meant "show me this transfer" (a notification, the activity log, a GRN's STR tag).
   */
  const act = sp.get('act');
  /* The link is acted on ONCE. For a Warehouse Manager that decision reads their warehouse list, so it waits for the
     list: a transfer register that arrived first used to fail the "your end?" test and open the read-only record with
     only Close, for good — Imran's "New transfer request" landed there on a slow load (DEF-061, 15 Sep). */
  const focus = useFocusRow(isWarehouseManager && !warehouseScopeKnown ? undefined : data, {
    match: ['id', 'transferNo'],
    onFocus: (r) => {
      // Same warehouse scope as the row buttons: a link is just another door onto the same act, and
      // arriving through it must not open a modal whose submit the server will refuse.
      if (act === 'dispatch' && canDispatch && myWarehouse(r.fromWarehouseId)) setDispatchFor(r);
      else if (act === 'receive' && canReceive && myWarehouse(r.toWarehouseId)) setReceiveFor(r);
      else if (act === 'resolve' && canManage) setDiscrepancyFor(r);
      else setDetailFor(r);
    },
  });
  const { data: products } = useQuery({
    queryKey: ['products-list'],
    queryFn: () => requestAllPages('/products'),
  });
  const productList = useMemo(() => products?.data ?? [], [products]);
  /* The catalogue row behind a transfer line, purely so the line can show the pack it moves. Built
     from the list this page already loads for its product filter — no extra request. */
  const prodMap = useMemo(
    () => new Map<string, any>(productList.map((p: any) => [String(p.id), p])),
    [productList],
  );

  const whName = whLabelOf;
  /**
   * ONE END OF THE JOURNEY, AS THE REGISTER DRAWS IT — the code as a chip, the site name beside it.
   *
   * The same two facts `whEnd` joins for prose, kept apart here. Dot-joined, "WH-BLR · Bengaluru
   * Warehouse" reads as one run-on string and the code — the half a transfer register is actually
   * scanned for — dissolves into it. Given its own shape and its own identity hue (WH-MUM is that
   * colour here, in every warehouse picker, and beside an avatar anywhere else), From and To become
   * two columns of colour the eye can run straight down. Bound to this page's fallback lookup —
   * see `whEnd` for why the row's own names are preferred over resolving the id in the browser.
   */
  const whCell = (r: any, end: 'from' | 'to') => {
    const { code, name } = whParts(r, end, whName);
    if (!code && !name) return <span className="text-subtle">—</span>;
    /*
     * The ROW's own manager list first, the client lookup only as a fallback.
     *
     * The lookup holds a Warehouse Manager's OWN godowns, and every transfer has another end that is
     * somebody else's — so resolving in the browser came back empty for the counterparty and this
     * cell announced "no manager assigned" about a warehouse that has one. The server sends the
     * managers of both ends for exactly that reason, the same way it already sends both names.
     */
    const rowManagers = end === 'from' ? r.fromManagers : r.toManagers;
    const wh = byWhId.get(String(end === 'from' ? r.fromWarehouseId : r.toWarehouseId));
    const managers: any[] | null = rowManagers ?? wh?.managers ?? null;
    const lead = managers?.[0];
    return (
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          {code ? <WarehouseCode code={code} /> : null}
          {name ? <span className="min-w-0 truncate">{name}</span> : null}
        </span>
        {/*
          * WHO TO CALL ABOUT THIS END.
          *
          * A transfer names two godowns, and on every row at least one of them is somebody else's —
          * so "who do I ring about this?" is the question the row raises and could not answer. The
          * face carries the manager's own identity hue (the same one their avatar has everywhere
          * else), which is what makes a column of these scannable rather than a column of grey
          * names; it VARIES per person, so it is identity rather than decoration. The name links to
          * their staff record, except for a Super Admin, which the shared link component refuses.
          */}
        {lead ? (
          <span className="flex min-w-0 items-center gap-1 ps-0.5">
            <EntityAvatar name={lead.name} src={lead.photoUrl} size={16} />
            <UserLink id={lead.id} role={lead.role}>
              <span className="min-w-0 truncate type-caption text-subtle">{lead.name}</span>
            </UserLink>
            {managers.length > 1 && (
              <Tooltip label={managers.slice(1).map((m: any) => m.name).join(', ')}>
                <span className="shrink-0 type-caption text-subtle">+{managers.length - 1}</span>
              </Tooltip>
            )}
          </span>
        ) : managers ? (
          /* A godown with nobody assigned is a GAP, not a blank: transfers to it have no one to
             acknowledge, pack or receive them. Named in amber so it reads as something to fix
             rather than as missing data.
             Only said when the server actually TOLD us the list is empty — `null` means this reader
             could not be given it, which is not the same fact and must not be dressed as one. */
          <span className="ps-0.5 type-caption text-warning-600 dark:text-harvest-300">no manager assigned</span>
        ) : null}
      </span>
    );
  };

  const filtersActive = !!(search.trim() || fromWh || toWh || status || productId || range[0] || range[1]
    || waiting || batch.trim() || origin || variance || frozen);
  const clearFilters = () => {
    setSearch(''); setFromWh(undefined); setToWh(undefined); setStatus(undefined);
    setProductId(undefined); setRange([null, null]); setWaiting(undefined);
    setBatch(''); setOrigin(undefined); setVariance(undefined); setFrozen(undefined);
  };

  // ── The filter panel ─────────────────────────────────────────────────────
  const whOptions = useMemo(() => (warehouses ?? []), [warehouses]);
  const whTextOf = (id?: string) => warehouseText(whOptions.find((w: any) => String(w.id) === String(id)) ?? {});
  /*
   * A PRODUCT OPTION SAYS THE PRODUCT ONCE.
   *
   * Dot-joining name, variant and SKU printed the same fact twice down the whole list — "AGN 1L ·
   * AGN 1L", "AGN 0.5L · AGN 500ml", "AGN LTE 0.25L · AGN LTE 250ml" — because a SKU is minted FROM
   * the name and pack, in whichever spelling of the size that catalogue row happens to use. The
   * shared `productLabel` compares them on the unit-normalised token, so 0.5L and 500ml are
   * recognised as the same size, and it keeps the LONGER of the two when one contains the other.
   * What survives is a SKU that genuinely says something new — and that is an identifier, so it
   * takes a chip of its own rather than being dot-joined onto the name it is not part of.
   */
  const productParts = (p: any) =>
    productLabel(p?.variantLabel ? `${p.name} ${p.variantLabel}` : p?.name, p?.sku);
  const productText = (p: any) => {
    const { primary, secondary } = productParts(p);
    return [primary, secondary].filter(Boolean).join(' · ');
  };
  const ORIGIN_LABEL: Record<string, string> = { manual: 'By a person', order: 'For an order' };
  const VARIANCE_LABEL: Record<string, string> = { open: 'Unsettled', any: 'Ever had one' };
  const WAITING_LABEL: Record<string, string> = { dispatch: 'To dispatch', receive: 'To receive', settle: 'To settle' };

  /* WHAT IS NARROWING THE LIST STAYS IN THE TOOLBAR, panel open or shut — you should never have to
     re-open the filters to find out why the register is short. Each chip drops its own filter. */
  const chips = [
    fromWh ? { key: 'from', label: `From: ${whTextOf(fromWh)}`, onRemove: () => setFromWh(undefined) } : null,
    toWh ? { key: 'to', label: `To: ${whTextOf(toWh)}`, onRemove: () => setToWh(undefined) } : null,
    range[0] || range[1] ? {
      key: 'when',
      label: `Date: ${range[0] ? formatIstDate(range[0]) : 'any'} → ${range[1] ? formatIstDate(range[1]) : 'today'}`,
      onRemove: () => setRange([null, null]),
    } : null,
    productId ? {
      key: 'product',
      label: `Product: ${productText(productList.find((p: any) => String(p.id) === String(productId)) ?? { name: productId })}`,
      onRemove: () => setProductId(undefined),
    } : null,
    batch.trim() ? { key: 'batch', label: `Batch: ${batch.trim()}`, onRemove: () => setBatch('') } : null,
    status ? { key: 'status', label: `Status: ${status.replace(/_/g, ' ')}`, onRemove: () => setStatus(undefined) } : null,
    waiting ? { key: 'waiting', label: `Waiting on: ${WAITING_LABEL[waiting] ?? waiting}`, onRemove: () => setWaiting(undefined) } : null,
    variance ? { key: 'variance', label: `Difference: ${VARIANCE_LABEL[variance] ?? variance}`, onRemove: () => setVariance(undefined) } : null,
    frozen ? { key: 'frozen', label: 'Frozen: cancellation pending', onRemove: () => setFrozen(undefined) } : null,
    origin ? { key: 'origin', label: `Raised: ${ORIGIN_LABEL[origin] ?? origin}`, onRemove: () => setOrigin(undefined) } : null,
  ].filter(Boolean) as { key: string; label: string; onRemove: () => void }[];

  /*
   * ELEVEN FILTERS DO NOT FIT ON A TOOLBAR.
   *
   * They used to sit in one flat `Space wrap` — eleven boxes of eleven different widths, wrapping
   * onto two lines with one uniform gap between all of them. Nothing said that From WH and To WH are
   * the two ends of ONE journey, or that the two date boxes are ONE period; the reader had to work
   * out the grouping the layout was hiding.
   *
   * Now: search stays inline, everything else opens IN PLACE under the toolbar in named sections,
   * the sections following the COLUMNS left to right — Route (From/To), When (Date), What moved
   * (Product/Batch), Progress (Status/Waiting/Difference/Frozen) — and last the one question that
   * has no column, who raised it. A date pair is ONE control in one shell, not two boxes with a
   * dash between them.
   */
  const filters = useFilterPanel({
    onClear: clearFilters,
    chips,
    groups: [
      {
        title: 'Route',
        tone: IDENTITY_SERIES[1],
        icon: <ArrowRight size={12} />,
        /*
         * BOTH ENDS TAKE A DOUBLE CELL.
         *
         * A warehouse option is a fixed-width code chip plus the site name, and at one cell (9rem)
         * the chip and the chevron consumed the whole box — the name truncated to a bare sliver and
         * the master warehouse's gold capsule was hard-clipped mid-word ("WH‑MASTER" printed on top
         * of its own code). The selected value was then unreadable in the one control whose value
         * you most need to read back. Two cells each fits "WH-BLR · Bengaluru Warehouse" whole.
         */
        fields: [
          {
            label: 'From warehouse',
            span: 2,
            /* Each side greys out the warehouse already chosen on the OTHER — a transfer's source and
               destination must differ, so From = To can only ever return an empty register. Disabled
               rather than hidden, the same way the New Transfer dialog does it: the reader sees why
               it is unavailable instead of wondering where it went. */
            control: (
              <Select placeholder="Any warehouse" allowClear showSearch optionFilterProp="searchLabel"
                value={fromWh} onChange={setFromWh}
                options={whOptions.map((w: any) => ({
                  value: String(w.id), disabled: !!toWh && String(w.id) === String(toWh), ...warehouseOption(w),
                }))} />
            ),
          },
          {
            label: 'To warehouse',
            span: 2,
            control: (
              <Select placeholder="Any warehouse" allowClear showSearch optionFilterProp="searchLabel"
                value={toWh} onChange={setToWh}
                options={whOptions.map((w: any) => ({
                  value: String(w.id), disabled: !!fromWh && String(w.id) === String(fromWh), ...warehouseOption(w),
                }))} />
            ),
          },
        ],
      },
      {
        title: 'When',
        tone: IDENTITY_SERIES[2],
        icon: <CalendarClock size={12} />,
        fields: [{
          label: 'Transfer date',
          span: 2,
          control: (
            /* The kit's date field is a BUTTON, and a wrapping <label> does not name a button — so
               each half carries its own, the way the kit's own RangePicker names its two ends. */
            <FilterRange
              from={<DatePicker value={range[0]} placeholder="From" aria-label="Transfer date from"
                disabledDate={disableFromDate(range[1])} onChange={(d) => setRange([d, range[1]])} />}
              to={<DatePicker value={range[1]} placeholder="To" aria-label="Transfer date to"
                disabledDate={disableToDate(range[0])} onChange={(d) => setRange([range[0], d])} />}
            />
          ),
        }],
      },
      {
        title: 'What moved',
        tone: IDENTITY_SERIES[0],
        icon: <Boxes size={12} />,
        fields: [
          {
            label: 'Product',
            // A catalogue name plus its pack needs the room; at one cell every option ellipsised.
            span: 2,
            control: (
              <Select placeholder="Any product" allowClear showSearch optionFilterProp="searchLabel"
                value={productId} onChange={setProductId}
                /* THE PACK SHOT COMES WITH THE NAME. A catalogue of "AGN 1L / AGN 500ml / AGN LTE
                   250ml" is a list of near-identical strings, and the picker is used by people who
                   know the drum on the shelf far better than its SKU. A plain avatar, not the
                   openable ProductThumb: inside an option a click must choose the product, not open
                   an image viewer over the panel. Products with no photo fall back to their own
                   identity-coloured initial, so the column of marks still VARIES. */
                options={productList.map((p: any) => {
                  const { primary, secondary } = productParts(p);
                  return {
                    value: String(p.id),
                    label: (
                      <span className="flex min-w-0 max-w-full items-center gap-2">
                        <EntityAvatar name={primary} src={productImage(p)} square size={22}
                          icon={<Package size={10} />} />
                        <span className="min-w-0 truncate">{primary}</span>
                        {secondary ? <CodeChip className="shrink-0">{secondary}</CodeChip> : null}
                      </span>
                    ),
                    searchLabel: productText(p),
                    title: productText(p),
                  };
                })} />
            ),
          },
          {
            label: 'Batch no',
            /* Trace a lot: "where did this batch go?" is a recall question, and the answer is spread
               across transfers. Matched on the number printed on the drum. */
            control: <Input placeholder="e.g. B-2409" value={batch} onChange={(e) => setBatch(e.target.value)} />,
            hint: 'Where a lot went',
          },
        ],
      },
      {
        title: 'Progress',
        tone: IDENTITY_SERIES[5],
        icon: <Truck size={12} />,
        // The four status-shaped questions sit together: what state it is in, whose step it is
        // waiting on, whether what arrived matched, and whether a cancellation has frozen it.
        fields: [
          {
            label: 'Status',
            /*
             * THE PICKER SHOWS THE ROWS' OWN TAGS.
             *
             * It listed the raw enum as seven lines of shouting grey text — a vocabulary that
             * appears nowhere else on the page, above a Status column made entirely of coloured
             * tags. Pick "IN TRANSIT" and you then had to work out for yourself which of the gold
             * tags below you had asked for. Same tag, same tone, same words as the column, so the
             * option and the rows it produces are recognisably one thing; the plain-English meaning
             * rides along as the option's hover text and as what typing searches.
             */
            control: (
              <Select placeholder="Any status" allowClear showSearch optionFilterProp="searchLabel"
                value={status} onChange={setStatus}
                options={STATUSES.map((s) => ({
                  value: s,
                  label: <Tag tone={COLOR[s]} style={{ margin: 0 }}>{s.replace(/_/g, ' ')}</Tag>,
                  searchLabel: `${s.replace(/_/g, ' ')} ${STATUS_MEANING[s] ?? ''}`,
                  title: STATUS_MEANING[s] ?? s.replace(/_/g, ' '),
                }))} />
            ),
          },
          {
            label: 'Waiting on',
            /* THE SAME THREE TONES AS THE PRESSABLE CHIPS ABOVE THE TABLE — blue = awaiting
               dispatch, gold = in transit, red = a difference to settle. The chip you pressed, the
               option you picked and the status tags on the rows are then one colour, not three
               separate vocabularies for the same three queues. */
            control: (
              <Select placeholder="Any step" allowClear showSearch optionFilterProp="searchLabel"
                value={waiting} onChange={setWaiting}
                options={[
                  { value: 'dispatch', tone: COLOR.PREPARED, text: 'To dispatch', icon: <Truck className="size-3" />, why: 'Waiting on the source depot to send it' },
                  { value: 'receive', tone: COLOR.IN_TRANSIT, text: 'To receive', icon: <PackageCheck className="size-3" />, why: 'Gone, and not yet booked in at the far end' },
                  { value: 'settle', tone: COLOR.PARTIAL, text: 'To settle', icon: <AlertTriangle className="size-3" />, why: 'Arrived short or over, and nobody has ruled on it' },
                ].map((o) => ({
                  value: o.value,
                  label: <Tag tone={o.tone} icon={o.icon} style={{ margin: 0 }}>{o.text}</Tag>,
                  searchLabel: `${o.text} ${o.why}`,
                  title: o.why,
                }))} />
            ),
          },
          {
            label: 'Difference',
            control: (
              <Select placeholder="Any" allowClear showSearch optionFilterProp="searchLabel"
                value={variance} onChange={setVariance}
                options={[
                  { value: 'open', tone: 'red' as TagTone, text: 'Unsettled', why: 'A difference still open — stock unaccounted for' },
                  { value: 'any', tone: 'gold' as TagTone, text: 'Ever had one', why: 'Includes differences that were settled' },
                ].map((o) => ({
                  value: o.value,
                  label: <Tag tone={o.tone} icon={<AlertTriangle className="size-3" />} style={{ margin: 0 }}>{o.text}</Tag>,
                  searchLabel: `${o.text} ${o.why}`,
                  title: o.why,
                }))} />
            ),
            hint: 'Received ≠ dispatched',
          },
          {
            label: 'Frozen',
            /* The same red Ban tag the row itself wears when a cancellation is pending. */
            control: (
              <Select placeholder="Any" allowClear value={frozen} onChange={setFrozen}
                options={[{
                  value: 'yes',
                  label: <Tag tone="red" icon={<Ban className="size-3" />} style={{ margin: 0 }}>cancel pending</Tag>,
                  searchLabel: 'cancellation pending frozen',
                  title: 'A cancellation is waiting on a Super Admin — nothing can move on it',
                }]} />
            ),
            hint: 'Nothing can move on it',
          },
        ],
      },
      {
        title: 'Origin',
        tone: IDENTITY_SERIES[3],
        icon: <Wand2 size={12} />,
        fields: [{
          /*
           * "RAISED BY" PROMISED A PERSON AND LISTED A KIND.
           *
           * The options are "A person" / "An order (auto)" — which is not who raised it, it is HOW
           * it came to exist. Read as "Raised by", the first option answers nothing (which person?)
           * and the reader goes looking for a name picker that was never there. The filter itself
           * is right and useful; only its name was wrong, so the name is what changed.
           *
           * Two different kinds of transfer share this register: one a person raised, one the system
           * raised to rescue an order. They are read for different reasons, rarely at the same time.
           */
          label: 'How it was raised',
          control: (
            <Select placeholder="Either way" allowClear showSearch optionFilterProp="searchLabel"
              value={origin} onChange={setOrigin}
              options={[
                { value: 'manual', tone: 'purple' as TagTone, text: 'By a person', icon: <UserRound className="size-3" />, why: 'Somebody raised this transfer by hand' },
                { value: 'order', tone: 'blue' as TagTone, text: 'For an order', icon: <Wand2 className="size-3" />, why: 'Raised automatically to rescue a sales order short of stock' },
              ].map((o) => ({
                value: o.value,
                label: <Tag tone={o.tone} icon={o.icon} style={{ margin: 0 }}>{o.text}</Tag>,
                searchLabel: `${o.text} ${o.why}`,
                title: o.why,
              }))} />
          ),
        }],
      },
    ],
  });

  // Also refresh the badge counts so the Transfers tab / nav pill updates immediately after acting
  // (dispatch/receive/cancel/resolve all change the awaiting-dispatch / awaiting-receipt counts).
  const refresh = () => { qc.invalidateQueries({ queryKey: ['stock-transfers'] }); qc.invalidateQueries({ queryKey: ['badges'] }); };
  const onErr = (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed');
  const dispatchMut = useConfirmedMutation({ title: "Dispatch this transfer?", content: "Stock leaves the source warehouse now and is in transit until it is received.", okText: "Yes, dispatch" }, {
    mutationFn: ({ id, allocations }: { id: string; allocations: any[] }) => request({ url: `/stock-transfers/${id}/dispatch`, method: 'POST', data: { allocations } }),
    onSuccess: () => { message.success('Dispatched — stock left the source'); setDispatchFor(null); refresh(); },
    onError: onErr,
  });
  const receiveMut = useConfirmedMutation({ title: "Receive this transfer?", content: "The stock lands in the destination warehouse and becomes sellable.", okText: "Yes, receive" }, {
    mutationFn: ({ id, lines }: { id: string; lines?: any[] }) => request<any>({ url: `/stock-transfers/${id}/receive`, method: 'POST', data: { lines } }),
    onSuccess: (r: any) => { message.success(r?.status === 'PARTIAL' ? 'Received with a discrepancy — an admin will resolve it' : 'Received'); setReceiveFor(null); refresh(); },
    onError: onErr,
  });
  /*
   * The source depot's two pre-dispatch steps. The register offers them as the ladder itself —
   * acknowledge → pick & pack → dispatch, one forward action per row (see "NO DISPATCH NOW SHORTCUT"
   * below). The server still accepts a dispatch from REQUESTED/SOURCE_ACK, so nothing already in flight
   * is stranded; the register just no longer offers that leap.
   *
   * BOTH ASK FIRST. They were the only two row actions in this register that fired on the bare click:
   * a single mis-aimed tap on a 32px icon told the destination warehouse the goods were confirmed, or
   * that they were picked and standing on the dock, and neither claim has an undo. Everything else
   * here (dispatch, receive, settle, cancel) was already gated, so these two were the exception rather
   * than the design. The question names what the OTHER end of the transfer will be told, because that
   * is the consequence — not "are you sure".
   */
  const ackMut = useConfirmedMutation({
    title: 'Acknowledge this transfer?',
    content: 'Tells the destination warehouse that the source has this stock and can send it. It does not move any stock yet.',
    okText: 'Yes, acknowledge',
  }, {
    mutationFn: (id: string) => request({ url: `/stock-transfers/${id}/acknowledge`, method: 'POST' }),
    onSuccess: () => { message.success('Acknowledged — the destination can see the source has it'); refresh(); }, onError: onErr,
  });
  /*
   * THE CONFIRM NAMES THE LOTS IT IS CONFIRMING.
   *
   * It used to say only "Says the goods are off the shelf" — which reads identically whether the
   * batches were chosen when the transfer was raised or were never chosen at all. A reader looking at
   * that dialog has no way to tell which, and the register does not show a batch column either, so
   * the honest conclusion from the screen was "it is letting me certify a pick of nothing". Reported
   * exactly that way: *"batches are not picked yet still how SA can able to mark it as picked"* — of a
   * transfer that did in fact carry a batch.
   *
   * So the question now answers itself: the dialog lists the lot and quantity being confirmed. If it
   * cannot list one, the picker opens instead and this dialog is never reached.
   */
  const prepMut = useConfirmedMutation<unknown, unknown, { id: string; allocations?: any[]; lots?: string }>(
    ({ lots }) => ({
      title: 'Mark this transfer picked and packed?',
      content: lots
        ? `Confirms ${lots} is off the shelf and waiting for a vehicle. Still no stock movement — that happens on dispatch.`
        : 'Says the goods are off the shelf and waiting for a vehicle. Still no stock movement — that happens on dispatch.',
      okText: 'Yes, it is packed',
    }), {
    /* `allocations` is the PICK. Sent when the transfer's lots were still unchosen — the server
       refuses "picked and packed" without them, because it would otherwise be a claim about goods
       nobody had identified. Omitted when the batches were already chosen as the transfer was
       raised: there is nothing left to decide, so the act is just a confirmation. */
    mutationFn: ({ id, allocations }: { id: string; allocations?: any[]; lots?: string }) =>
      request({ url: `/stock-transfers/${id}/prepare`, method: 'POST', data: allocations ? { allocations } : {} }),
      onSuccess: () => { message.success('Marked picked and ready to load'); setPrepareFor(null); refresh(); },
      onError: onErr,
    },
  );
  /*
   * CANCELLING IS AN ASK, NOT A CLICK.
   *
   * The server decides which it is: whoever holds STOCK.TRANSFER.CANCEL.APPROVE cancels straight
   * through, everyone else raises a request that a Super Admin rules on, and the transfer freezes
   * meanwhile. Both answers come back from the same call — `requested: true` distinguishes them — so
   * the client never has to guess its own authority, and a permission change needs no release here.
   *
   * A reason is mandatory either way, which is why this is a dialog and not a Popconfirm: both
   * warehouse managers will read that sentence, and an approver decides on it.
   */
  const cancelMut = useConfirmedMutation<any, unknown, { id: string; reason: string }>(
    /* The last gate, and it asks a DIFFERENT question depending on who is pressing it — "this ends
       the transfer" is a lie for the majority whose click only raises a request. Same shape as
       dispatch and receive above: the dialog collects the detail, this confirms the act. */
    canApproveCancel
      ? {
        title: 'Cancel this transfer for good?',
        content: 'Stock already dispatched is credited back to the source. A cancelled transfer cannot be reopened — a new one has to be raised.',
        okText: 'Yes, cancel it', danger: true,
      }
      : {
        title: 'Send this to the Super Admin?',
        content: 'They decide on your reason. Until they do, the transfer is frozen — nobody can acknowledge, prepare, dispatch or receive it.',
        okText: 'Yes, send it',
      },
    {
      mutationFn: ({ id, reason }: { id: string; reason: string }) =>
        request<any>({ url: `/stock-transfers/${id}/cancel`, method: 'POST', data: { reason } }),
      onSuccess: (r: any) => {
        message.success(r?.requested
          ? 'Sent to the Super Admin — the transfer is frozen until they decide'
          : 'Transfer cancelled');
        setCancelFor(null);
        refresh();
      },
      onError: onErr,
    },
  );
  const resolveMut = useConfirmedMutation({ title: "Resolve this transfer discrepancy?", content: "The stock figures are settled on your decision and the transfer is closed.", okText: "Yes, resolve" }, {
    mutationFn: ({ id, resolution, notes }: { id: string; resolution: string; notes?: string }) => request({ url: `/stock-transfer-discrepancies/${id}/resolve`, method: 'POST', data: { resolution, notes } }),
    onSuccess: () => { message.success('Discrepancy resolved'); qc.invalidateQueries({ queryKey: ['transfer-discrepancies'] }); refresh(); },
    onError: onErr,
  });

  /*
   * The wrong-batch decision. Its own mutation, not a flavour of `resolveMut`: a different person
   * makes it (the SENDING depot, or a Super Admin on a dispute), it answers a different question,
   * and confirming posts a correction across BOTH warehouses' books rather than settling a quantity.
   * The Popconfirms inside the dialog already spell out each outcome, so this one does not ask again.
   */
  const lotMut = useMutation({
    mutationFn: ({ id, decision, notes }: { id: string; decision: 'CONFIRM' | 'DISPUTE' | 'DISMISS'; notes?: string }) =>
      request({ url: `/stock-transfer-discrepancies/${id}/lot-decision`, method: 'POST', data: { decision, notes } }),
    onSuccess: (_r: any, v) => {
      message.success(v.decision === 'CONFIRM'
        ? 'Lots corrected at both warehouses — total stock is unchanged'
        : v.decision === 'DISPUTE' ? 'Disputed — a Super Admin will decide' : 'Not upheld — stock stays on the dispatched lot');
      qc.invalidateQueries({ queryKey: ['transfer-discrepancies'] });
      refresh();
    },
    onError: onErr,
  });

  // Row actions are ICON-ONLY squares (tooltip + aria-label carry the wording) — a labelled
  // "Resolve discrepancy" alone ran ~170px and the action column dwarfed the transfer it belonged to.
  // Tooltip outside, Popconfirm wrapping the Button directly (Radix chains the confirm onto the
  // button's own onClick).
  const cancelBtn = (r: any) => (
    // No Popconfirm any more: a cancellation now needs a written reason, and a reason cannot be
    // collected in a yes/no bubble. The dialog states what cancelling does at THIS transfer's stage
    // and whether the reader is cancelling it or asking for it to be cancelled.
    <Tooltip key="cancel" label={r.cancelPending ? 'A cancellation is already waiting on the Super Admin' : 'Cancel transfer'}>
      <Button size="sm" variant="text" aria-label="Cancel transfer" disabled={!!r.cancelPending}
        onClick={() => setCancelFor(r)}
        icon={<Ban size={16} className="text-danger-600 dark:text-danger-500" />} />
    </Tooltip>
  );

  /* One string for both the hover text and the accessible name of the settle action, so the two
     cannot drift. Reads "Settle 2 open differences" — the same count the badge and the lane carry. */
  const resolveLabel = (r: any) => ((r.openDiscrepancies ?? 0) > 0
    ? `Settle ${r.openDiscrepancies} open ${r.openDiscrepancies === 1 ? 'difference' : 'differences'}`
    : 'Review this transfer\u2019s differences');

  const columns: DataTableColumn<any>[] = [
    { key: 'transferNo', title: 'Transfer No', dataIndex: 'transferNo', sortable: true, alwaysVisible: true },
    // From/To WH render joined warehouse names (not the raw id they'd sort by) — not server-sortable.
    /* The row NAMES both ends itself (the server ships `from/toWarehouseCode|Name`), falling back to
       the client's own lookup only for a payload that predates that. This is not cosmetic: a Warehouse
       Manager is scoped to their own depot and cannot read the warehouse master, so resolving the
       counterparty in the browser printed a raw `#380` on every transfer they were party to. */
    { key: 'fromWarehouseId', title: 'From WH', dataIndex: 'fromWarehouseId', render: (_v: any, r: any) => whCell(r, 'from') },
    /* The arrow LEADS the destination, exactly as the GRN register draws its own From/To pair. The
       two ends are one movement, and as two bare columns of text the register never said so — the
       eye read a name, crossed a gap and read another. Parked at the left edge of this column the
       arrows stack into a single line on the boundary the two ends already share, so every row
       reads "here → there" without spending a column on the word. */
    {
      key: 'toWarehouseId', title: 'To WH', dataIndex: 'toWarehouseId',
      render: (_v: any, r: any) => (
        <span className="flex min-w-0 items-center gap-2">
          <ArrowRight size={13} aria-hidden className="shrink-0 text-subtle" />
          {whCell(r, 'to')}
        </span>
      ),
    },
    {
      /*
       * THE DATE, AND THE TIME IT WAS RAISED UNDER IT.
       *
       * A busy depot raises a dozen transfers in a morning, so a column of identical "10 Sep 2026"
       * cells cannot tell them apart or say which came first — the register loses its order exactly
       * on the day it is being read most.
       *
       * The two are DIFFERENT FACTS and are drawn as two, not joined into one string: the date is
       * the transfer's own business date, the time comes from when the record was raised. They
       * usually agree; when they do not, the time is captioned with its own date so it cannot be
       * misread as a time on the line above it.
       */
      key: 'transferDate', title: 'Date', dataIndex: 'transferDate', sortable: true,
      render: (v: string, r: any) => {
        const sameDay = r?.createdAt && formatIstDate(r.createdAt) === formatIstDate(v);
        return (
          <div className="leading-tight">
            <div>{formatIstDate(v)}</div>
            {r?.createdAt && (
              <div className="type-caption text-subtle">
                {sameDay ? formatIstTime(r.createdAt) : `raised ${formatIst(r.createdAt)}`}
              </div>
            )}
          </div>
        );
      },
    },
    // Items / Total Qty are computed post-query from the lines — not SQL-expressible here, so not sortable.
    { key: 'itemCount', title: 'Items', dataIndex: 'itemCount', align: 'right' },
    { key: 'totalQty', title: 'Total Qty', dataIndex: 'totalQty', align: 'right', render: (v: any) => fmtQty(v) },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      /* A frozen transfer's status is still true — REQUESTED, IN_TRANSIT — but it is no longer the
         whole truth: nothing can move on it. The freeze is stated BESIDE the status rather than
         replacing it, because the approver may refuse the cancellation and the row carries on. */
      render: (s: string, r: any) => (
        <span className="inline-flex flex-wrap items-center gap-1">
          <Tag tone={COLOR[s]}>{(s ?? '').replace(/_/g, ' ')}</Tag>
          {r.cancelPending && (
            <Tooltip label={`Cancellation requested — waiting on a Super Admin${r.cancelRequest?.reason ? `: ${r.cancelRequest.reason}` : ''}`}>
              <Tag tone="red" icon={<Ban className="size-3" />}>cancel pending</Tag>
            </Tooltip>
          )}
          {/* WHETHER THE LOTS ARE CHOSEN, on the row.
              A transfer can be raised with its batches already picked or with the choice left to the
              source, and nothing on the register distinguished the two — so "picked and packed" looked
              available on a transfer that (to the reader) had nothing behind it. Only worth saying
              before dispatch: from IN_TRANSIT on, every line necessarily carries a batch. */}
          {['REQUESTED', 'SOURCE_ACK', 'PREPARED'].includes(s) && unpickedLines(r) > 0 && (
            <Tooltip label="No source batch chosen yet — picking them is part of marking it packed, or of dispatching it">
              <Tag tone="gold" icon={<Package className="size-3" />}>lots to pick</Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      /*
       * HOW LONG this transfer has been sitting on the step it is waiting on.
       *
       * Dated from the act that produced the current state, not from creation (see
       * `transferWaitingSince` on the server): a transfer requested in July and dispatched in August
       * has been waiting on the DESTINATION since August, and ageing it from July would put a
       * month of the source's delay on the receiver's account.
       *
       * Blank for a finished or cancelled transfer — nothing is waiting, so nothing is late.
       * Computed after the query like Items / Total Qty, so not server-sortable.
       */
      // Narrow on purpose: the values are "today", "41 days" or a dash, and the table already
      // scrolls sideways — a wide column here would push Action off screen for everyone.
      key: 'waitingSince', title: 'Waiting', dataIndex: 'waitingSince', align: 'right', width: 104,
      render: (v: any) => {
        const d = waitingDays(v);
        if (d == null) return <span className="text-subtle">—</span>;
        return <Tag tone={AGE_TAG[ageBucket(d)]}>{waitingLabel(d)}</Tag>;
      },
    },
    { key: 'remarks', title: 'Remarks', dataIndex: 'remarks', defaultVisible: false, render: (v: any) => v || '—' },
    {
      key: 'receivedAt', title: 'Received At', dataIndex: 'receivedAt', sortable: true, defaultVisible: false,
      render: (v: string) => formatIst(v),
    },
    {
      key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, defaultVisible: false,
      render: (v: string) => formatIst(v),
    },
    ...(canAct ? [{
      key: 'actions', title: 'Action', alwaysVisible: true, width: 140,
      render: (_: any, r: any) => {
        const s = r.status;
        /*
         * FROZEN BY AN UNDECIDED CANCELLATION. The server refuses acknowledge / prepare / dispatch /
         * receive with 409 while a cancellation waits on a Super Admin, so the row must not offer
         * them — a button whose only possible outcome is an error is worse than no button. The row
         * says why instead, and Cancel stays visible (disabled) so the state is attributable to the
         * act that caused it rather than looking like a row that has simply lost its actions.
         */
        if (r.cancelPending) {
          return (
            <span onClick={(e) => e.stopPropagation()}>
              <Tooltip label={`Frozen: a cancellation is waiting on a Super Admin${r.cancelRequest?.reason ? ` — “${r.cancelRequest.reason}”` : ''}`}>
                <Tag tone="red" icon={<Ban className="size-3" />}>frozen</Tag>
              </Tooltip>
            </span>
          );
        }
        // The step this transfer is actually waiting on — one per status, so the leading slot always
        // means "move it forward" and cancel always sits to its right.
        const primary =
          (s === 'REQUESTED' && canDispatch && myWarehouse(r.fromWarehouseId)) ? (
            <Tooltip key="ack" label="Acknowledge — tell the destination the source has this">
              {/* A DOUBLE TICK — "seen, and I can fulfil it" — not an eye.
                  An eye means "look at this" everywhere else in the app (the GRN register's View
                  full details, the approvals hub's Open the record), so putting one on a MUTATION
                  said the opposite of what the button does: the reader was invited to peek and
                  instead told the destination warehouse the stock was confirmed. The four steps now
                  read as four different acts — seen (✓✓), packed (boxes), sent (truck), taken in
                  (package-check). */}
              <Button size="sm" variant="primary" aria-label="Acknowledge transfer" icon={<CheckCheck size={16} />}
                loading={ackMut.isPending} onClick={() => ackMut.mutate(r.id)} />
            </Tooltip>
          ) : (s === 'SOURCE_ACK' && canDispatch && myWarehouse(r.fromWarehouseId)) ? (
            /* "Picked and packed" is a claim about specific physical stock, so it needs a batch on
               every line — the server refuses otherwise. A transfer whose lots are still unchosen
               opens the batch picker first; one whose lots were named when it was raised only asks
               for confirmation. Dispatch is offered once the transfer is PREPARED. */
            <Tooltip key="prep" label={unpickedLines(r) > 0
              ? 'Pick the batches and mark it packed'
              : 'Mark picked and ready to load'}>
              {/* Lots still unchosen → open the picker and ask which ones. Already chosen when the
                  transfer was raised → nothing left to decide, so just confirm.
                  (Keep these as JSX comments: a trailing `//` after the self-closing tag sits in
                  JSX *children* and React renders it as literal text on the row.) */}
              {/* TWO ACTS, TWO GLYPHS. Both branches wore the same Boxes icon, so two SOURCE_ACK rows
                  side by side offered an identical green button where one OPENS A PICKER you can back
                  out of and the other POSTS THE RECORD on the spot. The riskier one was the one you
                  could not tell apart. Package-search = go and choose the lots; boxes = packed, which
                  is the word this step contributes to the ladder above (seen → packed → sent → taken
                  in). The gold "lots to pick" chip on the same row says the same thing in words. */}
              <Button size="sm" variant="primary"
                aria-label={unpickedLines(r) > 0 ? 'Pick the batches and mark transfer prepared' : 'Mark transfer prepared'}
                icon={unpickedLines(r) > 0 ? <PackageSearch size={16} /> : <Boxes size={16} />}
                loading={prepMut.isPending}
                onClick={() => (unpickedLines(r) > 0
                  ? setPrepareFor(r)
                  : prepMut.mutate({ id: r.id, lots: pickedLots(r) }))} />
            </Tooltip>
          ) : (s === 'PREPARED' && canDispatch && myWarehouse(r.fromWarehouseId)) ? (
            <Tooltip key="disp" label="Dispatch transfer">
              <Button size="sm" variant="primary" aria-label="Dispatch transfer" icon={<Truck size={16} />} onClick={() => setDispatchFor(r)} />
            </Tooltip>
          ) : s === 'IN_TRANSIT' && canReceive && myWarehouse(r.toWarehouseId) ? (
            <Tooltip key="recv" label="Receive transfer">
              <Button size="sm" variant="primary" aria-label="Receive transfer" icon={<PackageCheck size={16} />} onClick={() => setReceiveFor(r)} />
            </Tooltip>
          ) : s === 'PARTIAL' && canManage ? (
            /* Names how many differences are open, because that is what the badge and the approvals
               lane count — the button and the numbers advertising it say the same thing. The count
               goes in the ARIA LABEL as well as the tooltip: on an icon-only button the aria-label
               IS the whole accessible name, so hover-only text tells a sighted reader how much work
               the row holds and a screen-reader user nothing. */
            <Tooltip key="disc" label={resolveLabel(r)}>
              <Button size="sm" variant="soft-danger" aria-label={resolveLabel(r)} icon={<AlertTriangle size={16} />} onClick={() => setDiscrepancyFor(r)} />
            </Tooltip>
          ) : null;
        /*
         * NO "DISPATCH NOW" SHORTCUT ANY MORE.
         *
         * It used to sit beside the finer step on REQUESTED and SOURCE_ACK rows, on the reasoning
         * that acknowledging and packing were courtesies rather than gates — a depot that simply
         * loads the van could send straight from REQUESTED.
         *
         * That reasoning died when raising a transfer stopped naming a lot. A request now carries no
         * batch, so "skip the pick steps" would skip the ONLY step that decides which stock goes;
         * the dispatch screen would then have to collect the pick anyway, and the row would be
         * offering two buttons that open the same picker at the same moment. Worse, it let a
         * transfer reach IN_TRANSIT having never been PREPARED, so the record showed goods leaving a
         * warehouse nobody had recorded picking them in.
         *
         * One forward action per row now: acknowledge → pick & pack → dispatch. The server still
         * ACCEPTS a dispatch from REQUESTED/SOURCE_ACK — that gate is unchanged, so nothing in
         * flight is stranded and the approvals hub's own "Pick batches & send" still works — this
         * only stops the register offering the leap.
         */
        // Cancel returns the stock to its SOURCE, and that is the end the server scopes it to — so it
        // belongs to the sending manager, not the receiving one.
        const showCancel = canManage && myWarehouse(r.fromWarehouseId)
          && ['REQUESTED', 'SOURCE_ACK', 'PREPARED', 'IN_TRANSIT'].includes(s);
        if (!primary && !showCancel) return null;
        // Stop the row-click detail dialog opening when an action button is pressed.
        return <span onClick={(e) => e.stopPropagation()}><Space>{primary}{showCancel ? cancelBtn(r) : null}</Space></span>;
      },
    } as DataTableColumn<any>] : []),
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={1} style={{ margin: 0 }}>Stock Transfers</Typography.Title>}
        {canAct && waitingParts.length > 0 && (
          /*
           * EACH PART WEARS THE COLOUR OF THE ROWS IT COUNTS.
           *
           * The split used to be one grey sentence sitting above a table full of coloured status
           * tags — so the reader had to hold "2 to receive" in their head and go hunting for which
           * rows those were. Each chip now takes its tone from the STATUS tag of the transfers it
           * counts (blue = awaiting dispatch, gold = in transit, red = a difference to settle), so
           * the number and the rows behind it are the same colour and connect at a glance.
           */
          <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <span className="type-caption-strong text-content">{toDispatch + toReceive + toResolve} waiting on you</span>
            <span className="type-caption text-subtle">—</span>
            {/*
              * A COUNT YOU CAN PRESS.
              *
              * Naming the split told the reader what the badge was made of and then left them to
              * find those rows by hand, in a register of two hundred. Each chip filters to exactly
              * the transfers it counted — the server applies the same predicate the count uses, and
              * scopes it to the warehouse that decides that act, so the list can never come back a
              * different length from the number that was pressed. Pressing the active one clears it.
              */}
            {waitingParts.map((p) => (
              <Tooltip key={p.key} label={waiting === p.key ? 'Show every transfer again' : `Show only the ${p.n} ${p.label}`}>
                <button
                  type="button"
                  aria-pressed={waiting === p.key}
                  onClick={() => setWaiting(waiting === p.key ? undefined : p.key)}
                  className={cn(
                    /* `inline-flex` so the button hugs the chip it holds.
                       A default <button> is a block-ish box with its own line-height, so it stood
                       24px tall around a 17.8px chip — the three pressable counts rendered visibly
                       taller than the "oldest" chip beside them, which reads as three sizes of chip
                       on one line rather than one row of equals. */
                    'inline-flex rounded-pill outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-brand-500/40',
                    // The pressed chip wears a brand ring, so "which of these am I looking at?" is
                    // answered by the chip itself rather than by reading the URL.
                    waiting === p.key && 'ring-2 ring-brand-500/70',
                  )}
                >
                  <Tag tone={p.tone} style={{ margin: 0, cursor: 'pointer' }}>{p.n} {p.label}</Tag>
                </button>
              </Tooltip>
            ))}
            {/* A count says how MUCH is waiting and nothing about how long, and the two read very
                differently: eight transfers raised this week is a normal day, one sitting since July
                is stock nobody can account for. Server-computed like the counts beside it, so it
                states the page's oldest and not the current filter's. */}
            {oldestWaiting >= 1 ? (
              <Tooltip label="Counted from the step each transfer is waiting on — not from the day it was raised">
                <Tag tone={AGE_TAG[ageBucket(oldestWaiting)]} style={{ margin: 0 }}>oldest {waitingLabel(oldestWaiting)}</Tag>
              </Tooltip>
            ) : null}
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button size="sm" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
          {canManage && !embedded && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setFormOpen(true)}>
              New Transfer
            </Button>
          )}
        </div>
      </div>
      <Card>
        <DataTable
          storageKey="transfers"
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          rowClassName={focus.rowClassName}
          /* A ROW CLICK OPENS THE RECORD — it no longer unrolls a strip of product/batch/qty under
             the row. That strip repeated what the row already said (how many items, how much) while
             pushing every other transfer down the page, and it could never answer the questions a
             movement is actually read for: why it exists, who moved it at each step, and whether
             what arrived matches what left. All of that lives in TransferDetailModal now.
             The Action cell stops propagation, so Dispatch / Receive / Cancel still act directly. */
          onRow={(r: any) => ({ onClick: () => setDetailFor(r) })}
          /* The document number is what this register is searched by, so it stays on the toolbar
             where it can be typed into without opening anything. Everything else lives in the
             panel — see `filters` above for why. */
          toolbarLeft={
            <>
              <Input placeholder="e.g. STR/423128700" aria-label="Search by transfer no"
                prefix={<Search size={16} className="text-subtle" />}
                style={{ width: 240 }}
                value={search} onChange={(e) => setSearch(e.target.value)} />
              {filters.trigger}
              {filters.chips}
            </>
          }
          toolbarBelow={filters.panel}
        />
      </Card>
      <TransferFormModal open={formOpen} onClose={() => setFormOpen(false)} />
      {/* The record behind a row. Kept mounted only while a row is chosen, so the detail query is
          keyed to that transfer and refires when a different row is opened. */}
      {detailFor && (
        <TransferDetailModal
          open
          transferId={String(detailFor.id)}
          fallbackRow={detailFor}
          prodMap={prodMap}
          onClose={() => setDetailFor(null)}
          /* The same rule as the row's own Receive button: in transit, and this reader books stock in at
             the DESTINATION. The receipt dialog replaces this one rather than stacking over it. */
          onReceive={detailFor.status === 'IN_TRANSIT' && canReceive && myWarehouse(detailFor.toWarehouseId)
            ? () => { const r = detailFor; setDetailFor(null); setReceiveFor(r); }
            : undefined}
        />
      )}
      {cancelFor && (
        <CancelTransferModal transfer={cancelFor} loading={cancelMut.isPending} whName={whName}
          canDecide={canApproveCancel}
          onClose={() => setCancelFor(null)}
          onSubmit={(reason) => cancelMut.mutate({ id: cancelFor.id, reason })} />
      )}
      {prepareFor && (
        <DispatchModal transfer={prepareFor} loading={prepMut.isPending} whName={whName} mode="prepare"
          onClose={() => setPrepareFor(null)}
          onSubmit={(allocations) => prepMut.mutate({ id: prepareFor.id, allocations })} />
      )}
      {dispatchFor && (
        <DispatchModal transfer={dispatchFor} loading={dispatchMut.isPending} whName={whName}
          onClose={() => setDispatchFor(null)}
          onSubmit={(allocations) => dispatchMut.mutate({ id: dispatchFor.id, allocations })} />
      )}
      {receiveFor && (
        <ReceiveModal transfer={receiveFor} loading={receiveMut.isPending} prodMap={prodMap}
          onClose={() => setReceiveFor(null)}
          onSubmit={(lines) => receiveMut.mutate({ id: receiveFor.id, lines })} />
      )}
      {discrepancyFor && (
        <DiscrepancyModal transfer={discrepancyFor} resolving={resolveMut.isPending || lotMut.isPending}
          onClose={() => setDiscrepancyFor(null)}
          onResolve={(id, resolution, notes) => resolveMut.mutate({ id, resolution, notes })}
          onDecideLot={(id, decision, notes) => lotMut.mutate({ id, decision, notes })} />
      )}
      {/*
        * NO PERIOD PICKER — the register's own Transfer date filter IS the period.
        *
        * The dialog used to offer its own Start/End boxes beside a table that had just been
        * filtered by date, so the same question was asked twice with two different answers
        * possible: narrow the table to September, open Export, and the empty picker meant "all
        * time" — a file that did not match the rows it was launched from. One place to set the
        * dates, and it is the one you can see the result of.
        */}
      <ExportDialog
        hidePeriod
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export stock transfers"
        /*
         * SAY WHETHER THE FILTERS COME WITH IT.
         *
         * They do — the export runs the same query the table just ran — but the dialog gave no sign
         * of that, so someone who had narrowed the register to two transfers had no way to know
         * whether they were about to get two rows or seven hundred. Both readings are plausible and
         * only one is right, and the difference is a register handed to somebody else.
         */
        description={filtersActive
          ? `Exports the ${data?.length ?? 0} ${(data?.length ?? 0) === 1 ? 'transfer' : 'transfers'} matching the filters on screen — the document names them too. Excel adds a line-level tab and a tab of every difference.`
          : 'The whole transfer register. Excel adds a line-level tab and a tab of every difference reported on receipt.'}
        baseUrl="/stock-transfers/export"
        /* EVERY filter on screen goes with it, dates included — only the column sort is dropped,
           which decides the order of the rows and not which rows they are. */
        /* `sort` too: the column the reader sorted the register by is part of the view the
           file claims to be. Stripping it handed back a file in some other order. */
        baseParams={params}
      />
    </div>
  );
}

/**
 * CALLING A TRANSFER OFF — the ask, or the act, depending on who is reading.
 *
 * This replaced a Popconfirm, for two reasons that both come from the same place: cancelling a
 * transfer is not a small thing, and the old bubble treated it as one.
 *
 *  • A REASON IS NOW REQUIRED. Both warehouse managers read it afterwards, and for anyone who
 *    cannot decide their own cancellation, a Super Admin rules ON that sentence. A yes/no bubble
 *    has nowhere to type it.
 *  • WHAT CANCELLING DOES DEPENDS ENTIRELY ON THE STAGE, and the reader cannot be expected to hold
 *    that in their head. Before dispatch it voids a request and no stock moves. From IN_TRANSIT it
 *    credits goods back to the source that are physically sitting in a vehicle — the ledger will say
 *    they are home before anyone has carried them there. And if the transfer was raised to cover an
 *    order, killing it stops that order waiting and may leave it short. Each of those is stated for
 *    THIS transfer rather than left as a general warning.
 *
 * The button says which of the two acts is about to happen, because "Cancel transfer" would be a
 * lie for the nine people out of ten whose click only raises a request.
 */
function CancelTransferModal({ transfer, onClose, onSubmit, loading, canDecide, whName }: {
  transfer: any; onClose: () => void; onSubmit: (reason: string) => void; loading: boolean;
  /** Holds STOCK.TRANSFER.CANCEL.APPROVE — cancels outright instead of raising a request. */
  canDecide: boolean;
  whName: (id: any) => string;
}) {
  const [reason, setReason] = useState('');
  const inTransit = transfer.status === 'IN_TRANSIT';
  const forOrder = !!transfer.orderId;
  const why = reason.trim();
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} width={620}
      title={canDecide ? `Cancel transfer ${transfer.transferNo}` : `Request cancellation · ${transfer.transferNo}`}
      footer={<>
        <Button variant="default" onClick={onClose}>Keep the transfer</Button>
        <Button variant="danger" loading={loading} disabled={!why} onClick={() => onSubmit(why)}>
          {canDecide ? 'Cancel transfer' : 'Send to Super Admin'}
        </Button>
      </>}>
      <div className="space-y-3">
        <div className="rounded-card border border-line bg-surface-2 p-3">
          <div className="flex flex-wrap items-center gap-2 type-body-strong text-content">
            <span>{whEnd(transfer, 'from', whName)}</span>
            <ArrowRight className="size-4 text-subtle" />
            <span>{whEnd(transfer, 'to', whName)}</span>
            <Tag tone={COLOR[transfer.status]}>{(transfer.status ?? '').replace(/_/g, ' ')}</Tag>
          </div>
          <div className="mt-1 type-caption text-subtle">
            {transfer.itemCount ?? (transfer.lines?.length ?? 0)} {(transfer.itemCount ?? 0) === 1 ? 'product' : 'products'}
            {' · '}{fmtQty(transfer.totalQty)} units{transfer.transferDate ? ` · raised ${formatIstDate(transfer.transferDate)}` : ''}
          </div>
        </div>

        {/* What actually happens, for THIS transfer at THIS stage. */}
        <div className={cn('rounded-card border p-3',
          inTransit ? 'border-danger-500/30 bg-danger-500/[0.06]' : 'border-line bg-surface')}>
          <div className="type-body-strong text-content">
            {inTransit ? 'The goods have already left the source.' : 'Nothing has moved yet.'}
          </div>
          <ul className="mt-1.5 list-disc space-y-1 pl-5 type-caption text-subtle">
            <li>
              {inTransit
                ? `Cancelling credits this stock back to ${whEnd(transfer, 'from', whName)} — the ledger will show it home before anyone has physically returned it, so somebody has to make that true.`
                : 'Cancelling voids the request. No stock ledger entry is posted, at either end.'}
            </li>
            {forOrder && (
              <li>
                This transfer was raised to cover a customer order. Cancelling it stops that order waiting on
                the stock — it may be left short and have to be re-consolidated from somewhere else.
              </li>
            )}
            <li>A cancelled transfer cannot be reopened. A new one has to be raised.</li>
          </ul>
        </div>

        <div>
          <label htmlFor="cancel-reason" className="type-caption text-subtle">
            Why is it being called off? <span className="text-danger-600 dark:text-danger-400">Required</span>
          </label>
          <Textarea id="cancel-reason" rows={3} className="mt-1" value={reason} maxLength={500}
            placeholder={forOrder
              ? 'e.g. the order was cancelled, so the stock is no longer needed at the destination'
              : 'e.g. raised against the wrong warehouse — a fresh transfer has been created'}
            onChange={(e) => setReason(e.target.value)} />
          <p className="mt-1.5 type-caption text-subtle">
            {canDecide
              ? 'Both warehouse managers will read this on the transfer record.'
              : 'A Super Admin decides on this sentence. Until they do, the transfer is frozen — it cannot be acknowledged, prepared, dispatched or received.'}
          </p>
        </div>
      </div>
    </Modal>
  );
}

/**
 * A LOT, DRAWN ONE WAY, EVERYWHERE THE RECEIVE DIALOG NAMES ONE.
 *
 * The lot number as a chip with a package mark, its expiry beside it coloured by how close it is:
 * red past due, gold inside 90 days, green beyond. Extracted because the dialog draws a lot in two
 * places — the BATCH column and the "which lot actually arrived?" picker — and the picker was
 * printing bare text while the column two rows above it drew chips.
 *
 * (Deliberately NOT the `ExpiryChip` further down this file: that one belongs to the DISPATCH
 * dialog, states the date as plain text with a separate "12d left" tag, and treats anything past 90
 * days as neutral. This is the receive dialog's own presentation, unchanged — the point here was to
 * make one dialog agree with itself, not to restyle the other one.)
 */
function BatchChips({ batchNo, expDate }: { batchNo?: string | null; expDate?: string | null }) {
  if (!batchNo) return <span className="type-caption text-subtle">—</span>;
  const exp = expDate ? dayjs(expDate) : null;
  const days = exp ? exp.startOf('day').diff(dayjs().startOf('day'), 'day') : null;
  const tone: TagTone = days == null ? 'neutral' : days < 0 ? 'red' : days <= 90 ? 'gold' : 'green';
  const note = days == null ? '' : days < 0 ? ' · expired' : days <= 120 ? ` · ${days}d left` : '';
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      <Tag tone="neutral" icon={<Package className="size-3" />} style={{ margin: 0 }}>{batchNo}</Tag>
      {expDate && (
        <Tag tone={tone} icon={<CalendarClock className="size-3" />} style={{ margin: 0 }}>
          Exp {formatIstDate(expDate)}{note}
        </Tag>
      )}
    </span>
  );
}

/** Hold a typed figure inside a range, treating a cleared box as zero rather than NaN. */
const clampQty = (v: number, lo: number, hi: number) => Math.min(Math.max(Number.isFinite(v) ? v : 0, lo), Math.max(lo, hi));

// Destination-warehouse receive: per-line good/damaged/batch-mismatch. Short/damage/mismatch
// creates a discrepancy and holds the transfer PARTIAL until an admin resolves it.
function ReceiveModal({ transfer, onClose, onSubmit, loading, prodMap }: {
  transfer: any; onClose: () => void; onSubmit: (lines: any[]) => void; loading: boolean;
  /** Catalogue rows keyed by product id — passed in so the modal can show each pack's own picture
   *  without fetching a second copy of the product list. */
  prodMap: Map<string, any>;
}) {
  const lines: any[] = transfer.lines ?? [];
  /*
   * WRONG BATCH IS A QUANTITY, LIKE THE OTHER TWO.
   *
   * It used to be a tick, so a consignment of 20 with two crates under the wrong lot number and one
   * with all 20 under it recorded exactly the same thing. It is the one variance where the number
   * matters most: short stock is gone and damaged stock is written off, but mismatched stock is
   * SELLABLE — it is here, booked against the wrong lot, and putting it right means moving a
   * specific number of units. Without the figure, whoever settles it has to go and count the shelf.
   *
   * Mismatched units are a SUBSET of the good ones, never a fourth bucket: they arrived, so counting
   * them again beside good would make the line add up to more than was sent.
   */
  const [rows, setRows] = useState<Record<string, { received: number; damaged: number; mismatch: number; evidence: string[]; lots: { batchId: string; qty: number }[]; notes: string }>>(() => {
    const init: Record<string, any> = {};
    for (const l of lines) init[String(l.id)] = { received: Number(l.qty), damaged: 0, mismatch: 0, evidence: [], lots: [], notes: '' };
    return init;
  });
  /*
   * THE LOTS THE SOURCE COULD HAVE SENT, so a wrong batch can be NAMED rather than merely counted.
   *
   * Fetched from a transfer-scoped route, not the dispatch picker: this reader is the destination
   * manager, who has no scope over the source warehouse and would simply be refused by that one.
   * Keyed by product, because two lots of one product on the same transfer share the same candidates.
   */
  const { data: lotOptions } = useQuery({
    queryKey: ['transfer-mismatch-batches', transfer.id],
    queryFn: () => request<Record<string, any[]>>({ url: `/stock-transfers/${transfer.id}/mismatch-batches` }),
  });
  /** Dispatched quantity per lot — the ceiling every figure on that row is measured against. */
  const capOf = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lines) m.set(String(l.id), Number(l.qty) || 0);
    return m;
  }, [lines]);
  /*
   * THE THREE FIGURES ARE ONE COUNT, so the boxes hold each other in range.
   *
   * A `max` on a single input is advice about that input alone: it did not stop 3 good + 1 damaged
   * out of a consignment of 3, because each box was individually inside its own limit and the
   * contradiction existed only BETWEEN them. The server refuses that pair — but the receiver found
   * out after pressing the button, having already typed the whole receipt. So the state itself is
   * kept consistent and the impossible figure can never be entered in the first place.
   *
   * Damage takes precedence over good, because good starts pre-filled with the whole consignment
   * while damage is the deliberate assertion: mark 1 of 3 damaged and good falls to 2, which is what
   * actually happened. Pushing good back up is capped at what is not damaged, rather than silently
   * discarding the damage just recorded. Wrong-batch follows good down — those units are SOME OF the
   * good ones, so they can never outnumber them.
   */
  const set = (id: string, patch: Partial<{ received: number; damaged: number; mismatch: number; evidence: string[]; lots: { batchId: string; qty: number }[]; notes: string }>) => setRows((s) => {
    const cap = capOf.get(id) ?? 0;
    const n = { ...s[id], ...patch };
    if (patch.damaged != null) {
      n.damaged = clampQty(n.damaged, 0, cap);
      n.received = Math.min(n.received, cap - n.damaged);
    }
    if (patch.received != null) n.received = clampQty(n.received, 0, cap - n.damaged);
    n.mismatch = clampQty(n.mismatch, 0, n.received);
    /*
     * NAMED LOTS CANNOT OUTLIVE THE FIGURE THEY DESCRIBE.
     *
     * Drop the wrong-batch count from 3 to 1 and three assigned units become an impossible claim, so
     * the assignment is trimmed from the END — the rows the receiver added last are the ones they
     * were least sure of, and trimming beats silently resubmitting a total that no longer adds up.
     * Zero clears them outright: there is no wrong batch left to attribute.
     */
    if (patch.lots == null) {
      let budget = n.mismatch;
      const kept: { batchId: string; qty: number }[] = [];
      for (const a of n.lots ?? []) {
        if (budget <= 0) break;
        kept.push({ ...a, qty: Math.min(a.qty, budget) });
        budget -= Math.min(a.qty, budget);
      }
      n.lots = n.mismatch > 0 ? kept : [];
    }
    return { ...s, [id]: n };
  });
  /** Lots this line may name: the source's other batches of this product, each offered once. */
  const lotChoices = (l: any) => {
    const all = (lotOptions?.[String(l.productId)] ?? []) as any[];
    // The dispatched lot is not a wrong batch by definition, and a lot already named on this line
    // would double-count — a second entry for it says what raising the first one's quantity says.
    const taken = new Set((rows[String(l.id)]?.lots ?? []).map((a) => String(a.batchId)));
    /*
     * A LOT IS DRAWN THE WAY A LOT IS DRAWN — the same chips the BATCH column of this very dialog
     * uses, two rows up the screen. The picker printed bare text, so "GATE-E-EXPIRED-1788170006135"
     * and "RET-RET/169189973" arrived as an undifferentiated wall of monospace-less strings and the
     * receiver had to parse a lot number by reading it. The expiry chip matters most of all here:
     * it is the one thing that says an offered lot is already expired, and the list is ordered FEFO
     * by the server, so the colour explains the order.
     */
    return all.filter((b) => String(b.batchId) !== String(l.batchId)).map((b) => ({
      value: String(b.batchId),
      label: <BatchChips batchNo={b.batchNo} expDate={b.expDate} />,
      // The collapsed trigger is narrow — it shows the identity, not the whole story.
      selectedText: <BatchChips batchNo={b.batchNo} />,
      disabled: taken.has(String(b.batchId)),
      searchLabel: b.batchNo,
    }));
  };
  /** How many wrong-batch units are still unassigned on this line. */
  const unassigned = (l: any) => {
    const r = rows[String(l.id)];
    if (!r) return 0;
    return Number((r.mismatch - (r.lots ?? []).reduce((s, a) => s + (Number(a.qty) || 0), 0)).toFixed(3));
  };
  const anyDiscrepancy = lines.some((l) => {
    const r = rows[String(l.id)];
    return !!r && (r.received + r.damaged < Number(l.qty) - 1e-6 || r.damaged > 0 || r.mismatch > 0);
  });
  /*
   * DAMAGE WITHOUT A PHOTOGRAPH IS NOT A CLAIM, IT IS AN ASSERTION.
   *
   * Short stock and a wrong batch are both checkable against what the source says it sent. Damage is
   * not: it is asserted by the only person who can see the goods, and it is settled by a Super Admin
   * who was not at the unloading. Named per lot, because on a six-lot receipt "attach evidence" does
   * not tell anyone WHICH lot is still missing it.
   */
  const missingEvidence = lines.filter((l) => {
    const r = rows[String(l.id)];
    return !!r && r.damaged > 0 && (r.evidence ?? []).length === 0;
  });
  /*
   * EVERY WRONG-BATCH UNIT HAS TO BE ACCOUNTED FOR — but only where that is possible.
   *
   * Required when the source actually holds other lots of the product, because then the drum in the
   * receiver's hands carries one of those numbers and naming it is the whole point. NOT required
   * when the list is empty: a depot holding only the dispatched lot has nothing to offer, and a
   * receipt must never be blocked behind a picker with no options. The bare quantity still records
   * that something was wrong, which is what the register said before any of this existed.
   */
  const unnamedLots = lines.filter((l) => {
    const r = rows[String(l.id)];
    return !!r && r.mismatch > 0 && lotChoices(l).length > 0 && Math.abs(unassigned(l)) > 1e-6;
  });
  const submit = () => onSubmit(lines.map((l) => {
    const r = rows[String(l.id)];
    return {
      transferLineId: String(l.id), receivedQty: r.received, damagedQty: r.damaged,
      mismatchQty: r.mismatch, batchMismatch: r.mismatch > 0,
      // Only a damage claim carries photographs. Files left over from a damaged figure the receiver
      // then zeroed stay on screen (re-raising it gets them back) but are not sent, because they
      // would be evidence for a difference nobody is asserting.
      evidenceUrls: r.damaged > 0 ? r.evidence : undefined,
      // Only a real wrong-batch claim carries lots, and only complete ones: a partly-assigned list
      // would be refused by the server anyway, and the button is disabled until it balances.
      mismatchBatches: r.mismatch > 0 && (r.lots ?? []).length ? r.lots : undefined,
      // Only carried on a line that HAS a difference: a remark against a lot that arrived exactly as
      // sent has nothing to attach itself to, and would be written to a discrepancy row that is
      // never created.
      notes: (r.notes ?? '').trim() || undefined,
    };
  }));

  /*
   * ONE PRODUCT, ITS LOTS STACKED BESIDE IT.
   *
   * A transfer line is one LOT of one product, so a product drawn from three batches arrives as
   * three lines — and a flat row each printed "AGN 0.5L" three times down the column, which reads
   * as three different products until you compare the names character by character. The counting
   * still happens per lot (that is what a batch IS), but the product is named once and its lots
   * stack against it. Same shape as the transfer's own items table and the ledger below it.
   */
  const groups = useMemo(() => {
    const m = new Map<string, { productId: string; productName?: string; sku?: string; rows: any[]; total: number }>();
    for (const l of lines) {
      const k = String(l.productId);
      const g = m.get(k) ?? { productId: k, productName: l.productName, sku: l.sku, rows: [] as any[], total: 0 };
      g.rows.push(l);
      g.total += Number(l.qty) || 0;
      m.set(k, g);
    }
    return [...m.values()];
  }, [lines]);
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Receive transfer ${transfer.transferNo}`} width={920}
      footer={<>
        {/* A disabled button that does not say why is a dead end — name the lot still missing its
            photograph, so the fix is one glance away rather than a hunt down six rows. */}
        {missingEvidence.length > 0 ? (
          <span className="mr-auto flex items-center gap-1.5 type-caption text-danger-600 dark:text-danger-400">
            <AlertTriangle className="size-3.5 shrink-0" />
            Attach a photo of the damage on {missingEvidence.map((l: any) => l.batchNo || l.productName).join(', ')}
          </span>
        ) : unnamedLots.length > 0 ? (
          <span className="mr-auto flex items-center gap-1.5 type-caption text-danger-600 dark:text-danger-400">
            <AlertTriangle className="size-3.5 shrink-0" />
            {unnamedLots.map((l: any) => `${fmtQty(Math.abs(unassigned(l)))} on ${l.batchNo || l.productName}`).join(', ')}
            {' '}still needs the lot that actually arrived
          </span>
        ) : null}
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant={anyDiscrepancy ? 'danger' : 'primary'} loading={loading} disabled={missingEvidence.length > 0 || unnamedLots.length > 0} onClick={submit}>{anyDiscrepancy ? 'Receive with discrepancy' : 'Receive in full'}</Button>
      </>}>
      {/* ONE LINE, and the tricky rule moved to the column it applies to.
           This was a three-line paragraph that had to explain the whole model before the reader could
           type a number — including that wrong-batch units are a SUBSET of the good ones, which is a
           fact about ONE COLUMN and belongs under that column's heading, where it is read at the
           moment it matters rather than four sentences earlier. */}
      <Typography.Paragraph type="secondary">
        Enter what actually arrived. Short, damaged or wrong-batch units raise a difference for an admin to settle.
      </Typography.Paragraph>
      <div className="overflow-x-auto rounded-card border border-line">
        <table className="w-full min-w-[760px] border-collapse">
          <thead>
            <tr className="bg-surface-2 text-left">
              <th className="px-3 py-2 type-overline text-subtle">Product</th>
              <th className="px-3 py-2 type-overline text-subtle">Batch</th>
              <th className="px-3 py-2 pr-5 text-right type-overline text-subtle">Dispatched</th>
              <th className="px-3 py-2 text-right type-overline text-subtle">
                Received (good)
                <span className="block normal-case type-caption text-subtle">arrived sellable</span>
              </th>
              <th className="px-3 py-2 text-right type-overline text-subtle">
                Damaged
                {/* Stated here rather than in the intro: it is a fact about ONE column, read at the
                    moment the number is typed instead of four sentences earlier. */}
                <span className="block normal-case type-caption text-subtle">unusable · needs a photo</span>
              </th>
              <th className="px-3 py-2 text-right type-overline text-subtle">
                Wrong batch
                {/* The one rule people get wrong: these are SOME OF the good units, not extra ones. */}
                <span className="block normal-case type-caption text-subtle">of the good units</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => {
            // A lot with damage on it grows a second row underneath for the photographs, so the
            // product cell beside the group has to span those as well or the table shears.
            const extra = g.rows.reduce((n: number, x: any) => {
              const r = rows[String(x.id)];
              const wrong = !!r && (r.received + r.damaged < Number(x.qty) - 1e-6 || r.damaged > 0 || r.mismatch > 0);
              return n + ((r?.damaged ?? 0) > 0 ? 1 : 0) + ((r?.mismatch ?? 0) > 0 ? 1 : 0) + (wrong ? 1 : 0);
            }, 0);
            return g.rows.map((l: any, ri: number) => {
              const r = rows[String(l.id)];
              // A lot the receiver has already marked short or damaged, tinted while they type so the
              // consequence of the number they just entered is visible before they press the button.
              const problem = !!r && (r.received + r.damaged < Number(l.qty) - 1e-6 || r.damaged > 0 || r.mismatch > 0);
              const row = (
                <tr key={String(l.id)} className={cn(
                  // A hairline between lots of one product; a heavier rule where the next product
                  // starts, so the groups separate without needing a second colour.
                  ri > 0 && 'border-t border-line/70',
                  ri === 0 && gi > 0 && 'border-t-2 border-line-strong',
                  problem && 'bg-danger-500/[0.045]',
                )}>
                  {ri === 0 && (
                    <td rowSpan={g.rows.length + extra} className="border-r border-line bg-surface-2/40 px-3 py-2.5 align-top">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <ProductThumb name={g.productName ?? String(g.productId)} src={productImage(prodMap.get(String(g.productId)))} size={34} />
                        <div className="min-w-0">
                          <div className="truncate type-body-strong text-content">{g.productName ?? g.productId}</div>
                          {g.sku && !isRedundantSku(g.sku, g.productName) && <div className="truncate text-2xs text-muted">{g.sku}</div>}
                        </div>
                      </div>
                      {/* The per-product subtotal earns its place only when there is more than one lot
                          to add up — on a single lot it is the row's own figure printed again. */}
                      {g.rows.length > 1 && (
                        <div className="mt-1.5 type-caption text-subtle">
                          {g.rows.length} lots · <b className="text-content">{fmtQty(g.total)}</b> units
                        </div>
                      )}
                    </td>
                  )}
                  {/* The batch cell is the tallest thing in the row, so everything measured against
                      it sits on its CENTRE line — a figure floating at the top of a two-chip-high row
                      does not read as the pair of the input beside it. */}
                  <td className="px-3 py-2.5 align-middle">
                    <BatchChips batchNo={l.batchNo} expDate={l.expDate} />
                  </td>
                  <td className="px-3 py-2.5 pr-5 text-right align-middle type-body-strong text-content tabular-nums">{fmtQty(l.qty)}</td>
                  {/* Capped at what is NOT already damaged: good and damaged are two halves of one
                      consignment, so neither may be raised into the other's share of it. */}
                  <td className="px-3 py-2.5 text-right align-middle">
                    <InputNumber min={0} max={Math.max(0, Number(l.qty) - (r?.damaged ?? 0))} value={r?.received}
                      onChange={(v) => set(String(l.id), { received: Number(v ?? 0) })} style={{ width: 96 }} />
                  </td>
                  <td className="px-3 py-2.5 text-right align-middle">
                    <InputNumber min={0} max={Number(l.qty)} value={r?.damaged}
                      onChange={(v) => set(String(l.id), { damaged: Number(v ?? 0) })} style={{ width: 96 }} />
                  </td>
                  {/* Capped at the GOOD quantity — mismatched units are some of the ones that
                      arrived, so they can never outnumber them. */}
                  <td className="px-3 py-2.5 text-right align-middle">
                    <InputNumber min={0} max={Number(r?.received ?? l.qty)} value={r?.mismatch}
                      onChange={(v) => set(String(l.id), { mismatch: Number(v ?? 0) })} style={{ width: 96 }} />
                  </td>
                </tr>
              );
              /*
               * THE PHOTOGRAPHS, ON THEIR OWN ROW UNDER THE LOT THEY BELONG TO.
               *
               * Not a seventh column: it would be empty on every ordinary receipt — which is nearly
               * all of them — and too narrow to drop a file into on the rare one where it is not.
               * Appearing only once a damaged figure is entered also makes the requirement arrive at
               * the moment it is created, rather than sitting there as a box nobody knows is
               * mandatory until the button refuses.
               */
              const evidenceRow = (r?.damaged ?? 0) > 0 ? (
                <tr key={`${l.id}-evidence`} className="border-t border-dashed border-danger-500/40 bg-danger-500/[0.045]">
                  <td colSpan={5} className="px-3 pb-3 pt-2">
                    <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                      <span className="mt-1 inline-flex items-center gap-1.5 type-caption-strong text-danger-700 dark:text-danger-300">
                        <AlertTriangle className="size-3.5 shrink-0" />
                        Photo of the {fmtQty(r?.damaged)} damaged
                      </span>
                      <MultiFileUploadField category="evidence" max={5} buttonLabel="Add photo"
                        value={r?.evidence ?? []} onChange={(urls) => set(String(l.id), { evidence: urls })} />
                      {(r?.evidence ?? []).length === 0 && (
                        <span className="mt-1 type-caption text-subtle">Required — a Super Admin settles this write-off and needs to see what arrived.</span>
                      )}
                    </div>
                  </td>
                </tr>
              ) : null;
              /*
               * WHICH LOT ACTUALLY ARRIVED — one row per lot, because two wrong units can be two
               * different lots.
               *
               * A single dropdown would have forced "1 of BATCH/1 and 1 of BATCH/2" to be answered
               * with one of them, quietly losing the other and leaving whoever settles it correcting
               * the wrong stock. The running remainder is stated rather than left to arithmetic: the
               * receiver is standing at a pallet, not adding up a form.
               */
              const choices = lotChoices(l);
              const left = unassigned(l);
              const lotRow = (r?.mismatch ?? 0) > 0 ? (
                <tr key={`${l.id}-lots`} className="border-t border-dashed border-harvest-500/40 bg-harvest-500/[0.05]">
                  <td colSpan={5} className="px-3 pb-3 pt-2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                      <span className="inline-flex items-center gap-1.5 type-caption-strong text-harvest-700 dark:text-harvest-300">
                        <Boxes className="size-3.5 shrink-0" />
                        Which lot actually arrived?
                      </span>
                      {choices.length === 0 ? (
                        <span className="type-caption text-subtle">
                          The source holds no other lot of this product, so there is nothing to pick — the count alone is recorded.
                        </span>
                      ) : (
                        <span className={cn('type-caption', Math.abs(left) < 1e-6 ? 'text-subtle' : 'text-danger-600 dark:text-danger-400')}>
                          {Math.abs(left) < 1e-6
                            ? `all ${fmtQty(r?.mismatch)} accounted for`
                            : `${fmtQty(Math.abs(left))} of ${fmtQty(r?.mismatch)} still to assign`}
                        </span>
                      )}
                    </div>
                    {choices.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {(r?.lots ?? []).map((a, ai) => (
                          <div key={ai} className="flex flex-wrap items-center gap-2">
                            <Select size="md" showSearch optionFilterProp="searchLabel" placeholder="Pick the lot"
                              optionLabelProp="selectedText" popupMatchSelectWidth={false}
                              className="min-w-[240px]" value={a.batchId || undefined}
                              options={choices}
                              onChange={(v: any) => set(String(l.id), {
                                lots: (r?.lots ?? []).map((x, i) => (i === ai ? { ...x, batchId: String(v ?? '') } : x)),
                              })} />
                            <InputNumber min={0} max={Number(r?.mismatch ?? 0)} value={a.qty} style={{ width: 84 }}
                              onChange={(v: any) => set(String(l.id), {
                                lots: (r?.lots ?? []).map((x, i) => (i === ai ? { ...x, qty: Number(v ?? 0) } : x)),
                              })} />
                            <Button size="sm" variant="default" aria-label="Remove this lot" icon={<Eraser size={14} />}
                              onClick={() => set(String(l.id), { lots: (r?.lots ?? []).filter((_, i) => i !== ai) })} />
                          </div>
                        ))}
                        {/* Offered only while there is something left to assign AND a lot left to
                            assign it to — a button that can only produce an invalid row is noise. */}
                        {left > 1e-6 && choices.some((c) => !c.disabled) && (
                          <Button size="sm" variant="default" icon={<Plus size={14} />}
                            onClick={() => set(String(l.id), { lots: [...(r?.lots ?? []), { batchId: '', qty: left }] })}>
                            Add lot
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ) : null;
              /*
               * WHAT THE RECEIVER SAW, in their own words.
               *
               * The figures say WHAT is wrong; only the person at the pallet can say what it looked
               * like — seal broken, carton crushed and restacked, drums from two lots on one pallet.
               * Whoever settles this was not there, and on a short line the remark is the only
               * context that exists at all: there is no photograph to take of stock that is simply
               * not in the lorry.
               *
               * Shown ONLY on a line that has a difference. A lot that arrived exactly as sent has
               * nothing to remark on, and the remark would be attached to a discrepancy row that is
               * never created. Optional, deliberately: requiring prose before a receipt can be booked
               * would buy a form full of "ok" and a depot that dreads the screen.
               */
              const remarksRow = problem ? (
                <tr key={`${l.id}-notes`} className={cn('border-t border-dashed border-line', problem && 'bg-danger-500/[0.03]')}>
                  <td colSpan={5} className="px-3 pb-3 pt-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 type-caption-strong text-subtle">
                        <MessageSquare className="size-3.5 shrink-0" />
                        Remarks
                      </span>
                      <Input size="md" className="min-w-[260px] flex-1"
                        placeholder="What you saw — seal broken, carton crushed, drums from two lots on one pallet… (optional)"
                        value={r?.notes ?? ''}
                        onChange={(e) => set(String(l.id), { notes: e.target.value })} />
                    </div>
                  </td>
                </tr>
              ) : null;
              return (evidenceRow || lotRow || remarksRow)
                ? <Fragment key={String(l.id)}>{row}{evidenceRow}{lotRow}{remarksRow}</Fragment>
                : row;
            });
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// Admin resolution of a transfer's open discrepancies (ACCEPT_LOSS or RETURN_TO_SOURCE).
function DiscrepancyModal({ transfer, onClose, onResolve, onDecideLot, resolving }: { transfer: any; onClose: () => void; onResolve: (id: string, resolution: string, notes?: string) => void; onDecideLot: (id: string, decision: 'CONFIRM' | 'DISPUTE' | 'DISMISS', notes?: string) => void; resolving: boolean }) {
  const { data } = useQuery({
    queryKey: ['transfer-discrepancies', transfer.id],
    queryFn: () => request<any[]>({ url: '/stock-transfer-discrepancies', params: { transferId: transfer.id } }),
  });
  const [notes, setNotes] = useState<Record<string, string>>({});
  // Kept apart from the quantity ruling's notes: they are two decisions, often by two different
  // people, and sharing one box would put the source depot's remark on the Super Admin's write-off.
  const [lotNotes, setLotNotes] = useState<Record<string, string>>({});
  const open = (data ?? []).filter((d: any) => d.status === 'OPEN');
  /* Whether this reader is being asked to decide anything at all. A depot looking at its own damage
     claim is not — and telling it "each difference has to end one of two ways" while offering it
     neither is a screen describing somebody else's job as though it were theirs. */
  const canDecide = open.some((d: any) => d.canSettle !== false);
  /*
   * A PURE WRONG-BATCH SCREEN IS NOT A SCREEN ABOUT MISSING STOCK.
   *
   * "The destination counted less in than the source sent out … written off as a genuine loss" was
   * printed above a difference where nothing was short, nothing was damaged and the counted-in
   * figure equalled the dispatched one. Every clause of it was false, and it framed a clerical lot
   * correction as a loss — which is exactly the misreading that makes somebody reach for the wrong
   * button.
   */
  const onlyLots = open.length > 0
    && open.every((d: any) => Number(d.shortQty ?? 0) + Number(d.damagedQty ?? 0) <= 0);
  return (
    /*
     * ONE CARD PER DIFFERENCE, not a seven-column table.
     *
     * The table put six figures AND a notes box AND two buttons on one row: the row could not fit,
     * so the dialog grew a horizontal scrollbar and "Return to source" — half of the decision being
     * asked for — was clipped off the right edge. A reader could not even see both options, let
     * alone weigh them.
     *
     * A difference is a DECISION, not a data row, so it gets the shape of one: what came up short,
     * stated as an arithmetic the eye can check, then the note, then the two ways it can end. Every
     * control sits inside the dialog's own width at every size, and nothing scrolls sideways.
     */
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Differences · ${transfer.transferNo}`} width={840}
      footer={<Button variant="default" onClick={onClose}>Close</Button>}>
      <Typography.Paragraph type="secondary">
        {onlyLots
          ? 'Everything sent arrived — but some of it came under a different lot number than the paperwork. Nothing is missing and nothing is written off; the correction simply books those units against the lot that actually came, at both warehouses.'
          : open.length > 0 && !canDecide
            ? 'The destination counted less in than the source sent out. These are recorded and waiting on a Super Admin; the transfer completes once each one is settled.'
            : 'The destination counted less in than the source sent out. Each difference has to end one of two ways: written off as a genuine loss, or credited back to the source warehouse. Settling the last one completes the transfer.'}
      </Typography.Paragraph>
      {open.length === 0 ? (
        <div className="rounded-card border border-line bg-surface-2 p-4 type-body text-subtle">No open differences — everything on this transfer is settled.</div>
      ) : (
        <div className="space-y-3">
          {open.map((d: any) => (
            <div key={d.id} className="rounded-card border border-danger-500/30 bg-danger-500/[0.05] p-3.5">
              <div className="flex flex-wrap items-center gap-2">
                {/* Gold, not red, for a wrong batch: the stock is all there and sellable. Painting
                    a clerical misattribution the same colour as goods that never arrived tells the
                    reader something untrue before they have read a single figure. */}
                <Tag tone={String(d.type) === 'BATCH_MISMATCH' ? 'gold' : 'red'}>{(d.type ?? '').replace(/_/g, ' ')}</Tag>
                <span className="type-body-strong text-content">{d.productName ?? `#${d.productId}`}</span>
                {d.sku && !isRedundantSku(d.sku, d.productName) && <span className="type-caption text-subtle">{d.sku}</span>}
              </div>
              {/* Expected = received + damaged + short. Laid out in that order so the shortfall is
                  the figure the line ends on, which is the one being decided. */}
              <dl className="mt-3 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-4">
                <div><dt className="type-caption text-subtle">Sent out</dt><dd className="mt-0.5 type-body-strong text-content tabular-nums">{fmtQty(d.expectedQty)}</dd></div>
                <div><dt className="type-caption text-subtle">Counted in</dt><dd className="mt-0.5 type-body-strong tabular-nums text-brand-700 dark:text-brand-400">{fmtQty(d.receivedQty)}</dd></div>
                {/* Coloured only when there IS something to be alarmed about. A red 0 beside "Short"
                    reads as a shortfall at a glance, which on a wrong-batch row is the opposite of
                    the truth. */}
                <div><dt className="type-caption text-subtle">Damaged</dt><dd className={cn('mt-0.5 type-body-strong tabular-nums', Number(d.damagedQty ?? 0) > 0 ? 'text-harvest-700 dark:text-harvest-300' : 'text-subtle')}>{fmtQty(d.damagedQty)}</dd></div>
                <div><dt className="type-caption text-subtle">Short</dt><dd className={cn('mt-0.5 type-body-strong tabular-nums', Number(d.shortQty ?? 0) > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-subtle')}>{fmtQty(d.shortQty)}</dd></div>
              </dl>
              {/*
               * WHICH LOTS ARRIVED — and whose move it is.
               *
               * This is a SECOND decision on the row, independent of the quantity ruling below it:
               * nothing was lost, so writing units off or crediting them back says nothing about a
               * unit that arrived under the wrong number. It is shown as its own bordered block for
               * exactly that reason — two decisions side by side read as one until they are separated.
               */}
              {(d.mismatchBatches?.length ?? 0) > 0 && (
                <div className="mt-3 rounded-card border border-harvest-500/40 bg-harvest-500/[0.06] p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 type-caption-strong text-harvest-800 dark:text-harvest-200">
                      <Boxes className="size-3.5 shrink-0" />
                      Arrived under {d.mismatchBatches.length === 1 ? 'a different lot' : `${d.mismatchBatches.length} different lots`}
                    </span>
                    {d.lotStatus === 'CONFIRMED' && <Tag tone="green">corrected</Tag>}
                    {d.lotStatus === 'DISPUTED' && <Tag tone="red">disputed</Tag>}
                    {d.lotStatus === 'DISMISSED' && <Tag tone="neutral">not upheld</Tag>}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {d.mismatchBatches.map((a: any) => (
                      <Tag key={String(a.batchId)} tone="gold" icon={<Package className="size-3" />} style={{ margin: 0 }}>
                        {fmtQty(a.qty)} × {a.batchNo}
                      </Tag>
                    ))}
                  </div>
                  {/* What confirming DOES, said before it is pressed. "Correct the lots" gives no
                      hint that it writes to two warehouses' books, or that it moves no goods. */}
                  {d.lotStatus === 'PENDING' && (
                    <p className="mt-2 type-caption text-subtle">
                      Nothing was lost — these units arrived and are sellable, just booked against the lot on the
                      paperwork. Confirming moves them onto the lot that actually came, at <b className="text-content">both</b>
                      {' '}warehouses. Total stock does not change.
                    </p>
                  )}
                  {d.lotNotes && (
                    <div className="mt-2 type-caption text-subtle">
                      <span className="type-caption-strong text-content">Note: </span>{d.lotNotes}
                    </div>
                  )}
                  {d.lotCanDecide ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-harvest-500/30 pt-3">
                      <Input size="md" placeholder={d.lotStatus === 'DISPUTED' ? 'Why you are ruling this way (optional)' : 'Anything to note (optional)'}
                        className="min-w-[200px] flex-1"
                        value={lotNotes[d.id] ?? ''} onChange={(e) => setLotNotes((n) => ({ ...n, [d.id]: e.target.value }))} />
                      {d.lotStatus === 'PENDING' ? (
                        <>
                          <Popconfirm title="Not what you sent?"
                            description="Says your picking record shows the lot on the paperwork. Nothing is corrected and a Super Admin decides between the two depots."
                            onConfirm={() => onDecideLot(d.id, 'DISPUTE', lotNotes[d.id])}>
                            <Button size="md" variant="default" loading={resolving}>Dispute</Button>
                          </Popconfirm>
                          <Popconfirm title="Confirm the lots that arrived?"
                            description="Corrects the books at both warehouses so the stock sits against the lot that actually came. No goods move and no quantity changes."
                            onConfirm={() => onDecideLot(d.id, 'CONFIRM', lotNotes[d.id])}>
                            <Button size="md" variant="primary" loading={resolving}>Confirm lots</Button>
                          </Popconfirm>
                        </>
                      ) : (
                        <>
                          <Popconfirm title="Do not uphold the claim?"
                            description="Stock stays booked against the lot that was dispatched. Choose this when the source's picking record is the better evidence."
                            okButtonProps={{ danger: true }} onConfirm={() => onDecideLot(d.id, 'DISMISS', lotNotes[d.id])}>
                            <Button size="md" variant="soft-danger" loading={resolving}>Not upheld</Button>
                          </Popconfirm>
                          <Popconfirm title="Uphold the destination's count?"
                            description="Corrects the books at both warehouses onto the lots the destination reported. No goods move."
                            onConfirm={() => onDecideLot(d.id, 'CONFIRM', lotNotes[d.id])}>
                            <Button size="md" variant="primary" loading={resolving}>Correct the lots</Button>
                          </Popconfirm>
                        </>
                      )}
                    </div>
                  ) : d.lotStatus === 'PENDING' || d.lotStatus === 'DISPUTED' ? (
                    <div className="mt-3 flex items-start gap-2 border-t border-harvest-500/30 pt-3 type-caption text-subtle">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-harvest-600 dark:text-harvest-400" />
                      <span>
                        {d.lotWaitingOn === 'SUPER_ADMIN'
                          ? <>The two depots disagree, so a <b className="text-content">Super Admin</b> decides which lot actually arrived.</>
                          : <>Waiting on the <b className="text-content">sending depot</b> to confirm this against their picking record.</>}
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
              {/* THE PHOTOGRAPHS, BESIDE THE DECISION THEY EXIST TO INFORM.
                  Whoever settles this was not at the unloading; the only thing standing between
                  "damaged" and their signature is what the receiver photographed. */}
              {(d.evidenceUrls?.length ?? 0) > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="type-caption text-subtle">Damage photos</span>
                  <AttachmentCell files={d.evidenceUrls} label={`Damage · ${d.productName ?? d.productId}`} size={40} maxThumbs={4} />
                </div>
              )}
              {d.notes && <div className="mt-2.5 type-caption text-subtle"><span className="type-caption-strong text-content">Receipt note: </span>{d.notes}</div>}
              {/*
               * A DAMAGE WRITE-OFF IS NOT THIS READER'S TO MAKE.
               *
               * `canSettle` comes from the server, which knows who holds the permission — the client
               * does not re-derive the rule and cannot drift out of step with it. Compared against
               * `false` deliberately: if the field is ever absent the controls stay, because showing
               * a button that returns 403 is a smaller failure than hiding one that would have
               * worked. The reason is stated rather than the buttons merely vanishing, so nobody
               * concludes the screen is broken.
               */}
              {/*
               * A PURE WRONG-BATCH ROW HAS NO QUANTITY TO RULE ON.
               *
               * Nothing is short and nothing is damaged, so "write it off" and "credit it back" both
               * post exactly nothing — which is what they did before, silently, leaving the row
               * closed and the books wrong. Offering a choice that decides nothing invites somebody
               * to press it and believe they have dealt with the difference.
               */}
              {Number(d.shortQty ?? 0) + Number(d.damagedQty ?? 0) <= 0 ? null : d.canSettle === false ? (
                <div className="mt-3 flex items-start gap-2 border-t border-line/70 pt-3 type-caption text-subtle">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-harvest-600 dark:text-harvest-400" />
                  <span>Waiting on a <b className="text-content">Super Admin</b>. A damage write-off is settled centrally — the depot that reported the damage cannot also write it off.</span>
                </div>
              ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line/70 pt-3">
                <Input size="md" placeholder="Why you are deciding this (optional)" className="min-w-[220px] flex-1"
                  value={notes[d.id] ?? ''} onChange={(e) => setNotes((n) => ({ ...n, [d.id]: e.target.value }))} />
                {/* Secondary → primary, side by side: returning stock to the source is the reversible
                    outcome, writing it off is the one that ends in a loss. */}
                {/* The two rulings are not "undo" and "confirm" — they are two different claims about
                    where the goods physically are, and each posts (or declines to post) accordingly.
                    Spelling that out on the confirm is the difference between a considered decision
                    and a coin toss: the reader asked, of a settled one, "if 1 short then how is it
                    transferred in again to the source?" */}
                <Popconfirm title="Put it back on the source's books?"
                  description="Says the missing stock never actually left the source. It is credited back there and becomes sellable again — so only choose this if the goods are physically still in the source godown."
                  onConfirm={() => onResolve(d.id, 'RETURN_TO_SOURCE', notes[d.id])}>
                  <Button size="md" variant="default" loading={resolving}>Return to source</Button>
                </Popconfirm>
                <Popconfirm title="Write this off as a genuine loss?"
                  description="Says the stock left the source and never arrived. Nothing is posted — the source lost it at dispatch, so the loss already stands. The books simply close on it."
                  okButtonProps={{ danger: true }} onConfirm={() => onResolve(d.id, 'ACCEPT_LOSS', notes[d.id])}>
                  <Button size="md" variant="soft-danger" loading={resolving}>Accept loss</Button>
                </Popconfirm>
              </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/**
 * Days until a date, or null. Local midnight on both sides so "today" is 0 and not -1 at 00:30 IST.
 * (Same rule as the Stock page's batch table — one definition of "how close is this to expiring".)
 */
const daysToExpiry = (d?: string | null): number | null => {
  if (!d) return null;
  const t = new Date(`${String(d).slice(0, 10)}T00:00:00`).getTime();
  return Math.ceil((t - new Date().setHours(0, 0, 0, 0)) / 86_400_000);
};

/** Expiry, with how long is left on it. Red past due, amber inside 90 days, plain otherwise. */
function ExpiryChip({ date }: { date?: string | null }) {
  const d = daysToExpiry(date);
  if (!date) return <span className="type-caption text-subtle">no expiry</span>;
  const tone: TagTone = d == null ? 'neutral' : d < 0 ? 'red' : d <= 90 ? 'gold' : 'neutral';
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="text-content">{formatIstDate(date)}</span>
      {d != null && <Tag tone={tone}>{d < 0 ? 'expired' : d === 0 ? 'today' : `${d}d left`}</Tag>}
    </span>
  );
}

/**
 * SOURCE-WAREHOUSE DISPATCH — pick which batches of the source's stock actually go on the lorry.
 *
 * WHAT THIS SCREEN IS REALLY ASKING. Not "type some numbers": it is asking a warehouse manager to
 * commit specific physical stock to a specific journey. Three facts decide it — how much is still
 * unallocated, which batches expire soonest, and how much of each is on the shelf — and the previous
 * version put none of them where they could be read together. It opened with a paragraph of prose, a
 * bare `0 / 3` chip, and five identical zero boxes with no indication that the top batch was the one
 * to empty first.
 *
 * WHAT IT DOES NOW:
 *   • Says where the stock is GOING, at the top, because the whole point of the act is the journey.
 *   • Tracks the allocation as a filling bar per product and one for the whole transfer, so
 *     "am I done?" is answered by colour before it is answered by arithmetic.
 *   • Offers **Auto-fill**, which takes the requested quantity off the earliest-expiring batches
 *     first. FEFO is the rule a warehouse follows anyway, the API already returns the batches in
 *     that order, and doing it by hand across five batches is where mistakes come from.
 *   • Names what is still missing under the Dispatch button, so a disabled button explains itself
 *     instead of being a dead end.
 */
function DispatchModal({ transfer, onClose, onSubmit, loading, whName, mode = 'dispatch' }: {
  transfer: any; onClose: () => void; onSubmit: (allocations: any[]) => void; loading: boolean;
  whName: (id: any) => string;
  /**
   * THE SAME QUESTION, ASKED AT TWO MOMENTS.
   *
   * 'dispatch' commits the stock to the journey — it leaves the building and the ledger posts.
   * 'prepare' only records WHICH lots came off the shelf and were set by the door; nothing leaves the
   * books yet, and dispatch later pre-fills from this pick. Both need exactly the same answer (batch
   * and quantity per product, exactly covering the request) and the server validates them through one
   * shared rule, so they share one picker rather than two that could drift apart.
   */
  mode?: 'dispatch' | 'prepare';
}) {
  const isPrep = mode === 'prepare';
  const lines: any[] = transfer.lines ?? [];
  const products = useMemo(() => {
    const m = new Map<string, { productId: string; productName: string; sku: string; requested: number }>();
    for (const l of lines) {
      const k = String(l.productId);
      const e = m.get(k) ?? { productId: k, productName: l.productName ?? String(l.productId), sku: l.sku ?? '', requested: 0 };
      e.requested += Number(l.qty);
      m.set(k, e);
    }
    return [...m.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfer]);

  /** The lots the transfer itself named, so the list can say which pre-filled rows came from it. */
  const requestedBatchIds = useMemo(
    () => new Set(lines.filter((l: any) => l.batchId != null).map((l: any) => String(l.batchId))),
    [lines],
  );

  // alloc: productId -> (batchId -> qty). Seed from any batch the requester pre-picked at create (the new
  // create modal carries a batchId per line) so the dispatcher just confirms rather than re-picking; they
  // can still adjust if that batch has since been consumed.
  const [alloc, setAlloc] = useState<Record<string, Record<string, number>>>(() => {
    const seed: Record<string, Record<string, number>> = {};
    for (const l of lines) {
      if (l.batchId == null) continue;
      const pid = String(l.productId); const bid = String(l.batchId);
      seed[pid] = seed[pid] ?? {};
      seed[pid][bid] = (seed[pid][bid] ?? 0) + Number(l.qty);
    }
    return seed;
  });
  const setQty = (pid: string, bid: string, qty: number) => setAlloc((s) => ({ ...s, [pid]: { ...(s[pid] ?? {}), [bid]: qty } }));
  /** Replace a product's whole allocation at once — what Auto-fill and Clear both do. */
  const setProductAlloc = (pid: string, next: Record<string, number>) => setAlloc((s) => ({ ...s, [pid]: next }));
  const allocatedFor = (pid: string) => Object.values(alloc[pid] ?? {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const allValid = products.every((p) => Math.abs(allocatedFor(p.productId) - p.requested) < 1e-6);

  const totalRequested = products.reduce((a, p) => a + p.requested, 0);
  const totalAllocated = products.reduce((a, p) => a + allocatedFor(p.productId), 0);
  /* What is still missing, in words. A disabled button that will not say why is the thing people
     file a bug about; naming the product and the shortfall turns it into an instruction. */
  const missing = products
    .map((p) => ({ p, gap: p.requested - allocatedFor(p.productId) }))
    .filter((x) => Math.abs(x.gap) > 1e-6);

  const submit = () => {
    const allocations: any[] = [];
    for (const p of products) {
      for (const [bid, q] of Object.entries(alloc[p.productId] ?? {})) {
        if (Number(q) > 0) allocations.push({ productId: p.productId, batchId: bid, qty: Number(q) });
      }
    }
    onSubmit(allocations);
  };

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} width={880}
      title={isPrep ? `Pick & pack ${transfer.transferNo}` : `Dispatch transfer ${transfer.transferNo}`}
      footer={<>
        {/* The reason the button is off, beside the button that is off. */}
        <div className="mr-auto min-w-0 text-left type-caption">
          {allValid
            ? <span className="inline-flex items-center gap-1.5 text-brand-700 dark:text-brand-400"><PackageCheck size={15} />
              {isPrep ? ' Every product is picked — ready to mark packed.' : ' Every product is covered — ready to send.'}</span>
            : (
              <span className="text-warning-600 dark:text-harvest-300">
                Still to allocate:{' '}
                {missing.slice(0, 2).map((x, i) => (
                  <span key={x.p.productId}>
                    {i > 0 && ', '}
                    {x.gap > 0 ? `${fmtQty(x.gap)} more` : `${fmtQty(-x.gap)} too many`} of {x.p.productName}
                  </span>
                ))}
                {missing.length > 2 ? ` and ${missing.length - 2} more` : ''}
              </span>
            )}
        </div>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={loading} disabled={!allValid}
          icon={isPrep ? <Boxes size={16} /> : <Truck size={16} />} onClick={submit}>
          {isPrep ? 'Mark picked & packed' : 'Dispatch'}
        </Button>
      </>}>

      {/* THE JOURNEY, first — this act moves stock between two buildings, and which two is the fact
          the dispatcher is confirming. It was nowhere on the old dialog. */}
      <div className="mb-4 rounded-card border border-line bg-surface-2 p-3.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-control bg-brand-500/12 text-brand-700 dark:text-brand-400">
            <Truck size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate type-body-strong text-content">{whEnd(transfer, 'from', whName)}</span>
              <ArrowRight size={16} className="shrink-0 text-subtle" />
              <span className="truncate type-body-strong text-content">{whEnd(transfer, 'to', whName)}</span>
            </div>
            <div className="mt-0.5 type-caption text-subtle">
              {transfer.transferDate ? formatIstDate(transfer.transferDate) : '—'}
              {' · '}{products.length} {products.length === 1 ? 'product' : 'products'}
              {' · '}{fmtQty(totalRequested)} units requested
            </div>
          </div>
          {/* The whole-transfer meter. Green only when the last unit is placed. */}
          <div className="w-full min-w-[180px] sm:w-52">
            <div className="flex items-baseline justify-between type-caption">
              <span className="text-subtle">Allocated</span>
              <span className={cn('font-semibold', allValid ? 'text-brand-700 dark:text-brand-400' : 'text-content')}>
                {fmtQty(totalAllocated)} / {fmtQty(totalRequested)}
              </span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-pill bg-surface-3">
              <div
                className={cn('h-full rounded-pill transition-all', allValid ? 'bg-brand-500' : totalAllocated > totalRequested ? 'bg-danger-500' : 'bg-harvest-400')}
                style={{ width: `${totalRequested ? Math.min(100, (totalAllocated / totalRequested) * 100) : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <p className="mb-3 type-caption text-subtle">
        Pick the source batches for each product. Stock leaves {whEnd(transfer, 'from', whName)} on dispatch;
        {' '}{whEnd(transfer, 'to', whName)} raises a GRN when it arrives.
      </p>

      {products.map((p) => (
        <ProductBatchPicker key={p.productId} warehouseId={transfer.fromWarehouseId} product={p}
          requestedBatchIds={requestedBatchIds}
          alloc={alloc[p.productId] ?? {}} onQty={(bid, qty) => setQty(p.productId, bid, qty)}
          onReplace={(next) => setProductAlloc(p.productId, next)} allocated={allocatedFor(p.productId)} />
      ))}
    </Modal>
  );
}

function ProductBatchPicker({ warehouseId, product, alloc, onQty, onReplace, allocated, requestedBatchIds }: {
  warehouseId: string; product: { productId: string; productName: string; sku: string; requested: number };
  alloc: Record<string, number>; onQty: (batchId: string, qty: number) => void;
  onReplace: (next: Record<string, number>) => void; allocated: number;
  /** Lots the transfer itself named when it was raised — tagged in the list so the pre-filled
   *  quantity is attributable rather than looking arbitrary. Empty when nothing was pre-picked. */
  requestedBatchIds: Set<string>;
}) {
  const { data: batches, isLoading } = useQuery({
    queryKey: ['transfer-source-batches', warehouseId, product.productId],
    queryFn: () => request<any[]>({ url: '/transfer-source-batches', params: { warehouseId, productId: product.productId } }),
    enabled: !!warehouseId && !!product.productId,
  });
  const list = batches ?? [];
  const over = allocated > product.requested + 1e-6;
  const short = allocated < product.requested - 1e-6;
  const onHand = list.reduce((a: number, b: any) => a + Number(b.available ?? 0), 0);
  const cannotCover = onHand < product.requested - 1e-6;

  /*
   * FEFO in one press. The endpoint already returns batches earliest-expiry-first (nulls last) and
   * excludes anything past its date, so filling straight down the list IS the rule the warehouse
   * follows — this just stops it being five pieces of mental arithmetic. It REPLACES the current
   * allocation rather than adding to it, so pressing it twice is idempotent.
   */
  const autoFill = () => {
    let left = product.requested;
    const next: Record<string, number> = {};
    for (const b of list) {
      if (left <= 1e-6) break;
      const take = Math.min(Number(b.available ?? 0), left);
      if (take > 0) { next[String(b.batchId)] = take; left -= take; }
    }
    onReplace(next);
  };

  return (
    <div className="mb-3 overflow-hidden rounded-card border border-line">
      {/* Product header: identity on the left, how far along on the right. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface-2 px-3.5 py-2.5">
        {/* No mark: a Package glyph on every product header is the same picture each time (constant glyph on every row is not identity — see the colour-sweep brief).
            The name and its SKU tag identify the block. */}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="truncate type-body-strong text-content" title={product.productName}>{product.productName}</span>
            {product.sku && !isRedundantSku(product.sku, product.productName) && <Tag>{product.sku}</Tag>}
          </div>
          {/* WHAT THIS LIST IS. Every batch of this product sitting in the SOURCE warehouse — the
              shelf you are picking from — which is a different thing from the batches already on the
              transfer, and the two were easy to read as one. Asked, of a transfer carrying a single
              lot while this list showed three: "why on the SA side is there only 1 batch visible?"
              Nothing was missing; these are the lots you MAY take, that was the lot the transfer ASKED
              for. Saying so here, and tagging the requested row below, connects the two. */}
          <div className="mt-0.5 type-caption text-subtle">
            {fmtQty(onHand)} on hand at the source across {list.length} {list.length === 1 ? 'batch' : 'batches'} — pick from these
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip label="Take the requested quantity off the batches that expire soonest">
            <Button size="sm" variant="default" icon={<Wand2 size={15} />} disabled={!list.length || cannotCover} onClick={autoFill}>Auto-fill</Button>
          </Tooltip>
          {allocated > 0 && (
            <Tooltip label="Clear this product's allocation">
              <Button size="sm" variant="text" aria-label={`Clear the allocation for ${product.productName}`} icon={<Eraser size={15} />} onClick={() => onReplace({})} />
            </Tooltip>
          )}
          <div className="w-28">
            <div className="text-right type-caption">
              <span className={cn('font-semibold', over ? 'text-danger-600' : short ? 'text-warning-600 dark:text-harvest-300' : 'text-brand-700 dark:text-brand-400')}>
                {fmtQty(allocated)}
              </span>
              <span className="text-subtle"> / {fmtQty(product.requested)}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-pill bg-surface-3">
              <div className={cn('h-full rounded-pill transition-all', over ? 'bg-danger-500' : short ? 'bg-harvest-400' : 'bg-brand-500')}
                style={{ width: `${product.requested ? Math.min(100, (allocated / product.requested) * 100) : 0}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* A shortfall the source cannot fix is worth saying before the dispatcher counts the boxes. */}
      {!isLoading && cannotCover && (
        <div className="flex items-start gap-2 border-b border-line bg-warning-500/10 px-3.5 py-2 type-caption text-warning-700 dark:text-harvest-300">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>This warehouse holds {fmtQty(onHand)} of the {fmtQty(product.requested)} requested, and a transfer can only be sent in full. Either wait until more stock arrives here, or have this transfer cancelled and a smaller one raised.</span>
        </div>
      )}

      {isLoading ? (
        <div className="px-3.5 py-6 text-center type-caption text-subtle">Loading batches…</div>
      ) : !list.length ? (
        <div className="px-3.5 py-8 text-center">
          <div className="type-body text-content">No sellable stock here</div>
          <div className="mt-0.5 type-caption text-subtle">Nothing of this product is available in the source warehouse — expired batches are not offered.</div>
        </div>
      ) : (
        <div className="divide-y divide-line/70">
          <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-3.5 py-1.5 type-label text-subtle">
            {/* `pr-8` parks the header over the INPUT rather than the "All" button beside it. */}
            <span>Batch</span><span>Expiry</span><span className="w-16 text-right">Available</span><span className="w-[124px] pr-8 text-right">Allocate</span>
          </div>
          {list.map((b: any, i: number) => {
            const taken = Number(alloc[String(b.batchId)] ?? 0);
            const avail = Number(b.available ?? 0);
            return (
              <div key={b.batchId}
                className={cn('grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-3.5 py-2 transition-colors',
                  taken > 0 ? 'bg-brand-500/[0.07]' : 'hover:bg-surface-2')}>
                <span className="min-w-0 truncate font-mono text-xs text-content" title={b.batchNo}>
                  {b.batchNo}
                  {/* The batch the FEFO rule points at. Said once, on the row it applies to. */}
                  {/* `font-sans` because the tag sits inside the monospaced batch number and would
                      otherwise be set in it — a word of prose typeset as a code. */}
                  {i === 0 && list.length > 1 && <Tag tone="green" className="ml-2 font-sans">use first</Tag>}
                  {/* The lot the REQUESTER named when the transfer was raised. Without it the
                      pre-filled quantity looked arbitrary, and a dispatcher had no way to tell the
                      batch the transfer asked for from the two beside it that it did not. */}
                  {requestedBatchIds.has(String(b.batchId)) && (
                    <Tooltip label="The batch this transfer asked for — pre-filled for you. You can still pick a different lot.">
                      <Tag tone="blue" className="ml-2 font-sans">requested</Tag>
                    </Tooltip>
                  )}
                </span>
                <span className="type-caption"><ExpiryChip date={b.expDate} /></span>
                <span className="w-16 text-right tabular-nums text-content">{fmtQty(avail)}</span>
                <span className="flex w-[124px] items-center justify-end gap-1">
                  <InputNumber size="small" min={0} max={avail} value={taken} className="w-[76px]"
                    onChange={(v) => onQty(String(b.batchId), Number(v ?? 0))} />
                  <Tooltip label={`Take all ${fmtQty(avail)}`}>
                    <Button size="sm" variant="text" className="px-1.5" aria-label={`Allocate all of batch ${b.batchNo}`}
                      onClick={() => onQty(String(b.batchId), avail)}>All</Button>
                  </Tooltip>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
